import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  SelectorOptions,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { compose, patch, removeItem, updateItem } from '@ngxs/store/operators';
import {
  ArchiveDataEntryDtoModel,
  CommentControllerService,
  DataCollectionsControllerService,
  DataEntity,
  DataEntriesTags,
  DataEntry,
  DataEntryKanbanViewColumn,
  DataEntryTemplateExportControllerService,
  DataRelationControllerService,
  FileModel,
  GroupsControllerService,
  ReportEntryControllerService,
  TemplatesControllerService,
} from '@osos/shared/api';
import {
  ALL_ENTRY_VALIDATION_ERROR_CODE,
  ApiErrorModel,
  BreadcrumbToken,
  BulkUpdateResponsePresenterModel,
  BulkUpgradeResponsePresenterModel,
  BulkWorkflowActionExecutionResultModel,
  ChangeStepModeEnum,
  CUSTOM_VALIDATION_EXPRESSION_FAILED,
  DataEntityPresenterModel,
  DataEntityTypeEnum,
  DataEntryNavigationInfoModel,
  DataEntryPresenterModel,
  DataEntryStateEnum,
  DataEntryTreePresenterModel,
  DataEntryWorkflowStatusPresenterModel,
  DataQueryPresenterModel,
  DEFAULT_PAGESIZE,
  ENTITY_META_FIELDS,
  EntryAttachments,
  EntryEmailsPresenterModel,
  EntryHistoryPresenterModel,
  ExpressionTypeEnum,
  Filter,
  FilterExpressionValueTypeEnum,
  KanbanEntriesPresenterModel,
  LoadingIndicator,
  LoadingType,
  LocalizedValueModel,
  ManagedHttpContext,
  MappingFieldSourcesModel,
  OrderableEntityDefaultSortValue,
  Page,
  RefProperty,
  Sort,
  SPACE_USERS_CORE_ENTITY_ID,
  TemplateExportTypeEnum,
  TemplateTypeEnum,
  VALIDATION_ERROR_CODE,
  Where,
} from '@osos/shared/types';
import isEqual from 'lodash-es/isEqual';
import { EMPTY, forkJoin, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  mergeMap,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  DataEntryFactoryService,
  DataEntryHistoryFactoryService,
  EntityFactoryService,
  EntryEmailsFactoryService,
  EntryWorkFlowStatusFactoryService,
  FieldFactoryService,
  QueryFactoryService,
  TransformationFactoryService,
} from '../../factories';
import { DataEntryTreeFactoryService } from '../../factories/data-entry-tree-factory/data-entry-tree-factory.service';
import {
  ApiUtilsService,
  BreadcrumbService,
  DataQueryService,
  EntryDataService,
  TypesUtilsService,
  ViewDataService,
  WorkflowDataService,
} from '../../services';
import {
  buildEntriesStatusFilter,
  getMainTitleFieldValue,
  normalizePresentedEntry,
} from '../../utils';
import { CoreCountersActions } from '../core-counters/core-counters.actions';
import { CoreSpaceActions } from '../core-space/core-space.actions';
import { DashboardActions } from '../dashboard-state/dashboard.action';
import { EntityActions } from '../entity-data-state/entity.action';
import { EntityState } from '../entity-data-state/entity.state';
import { LoadingActions } from '../loading/loading.actions';
import { NavigationStateActions } from '../navigation/navigation.actions';
import { EntryActions } from './entry.action';

const WORKFLOW_ERROR_CODES = new Set([
  CUSTOM_VALIDATION_EXPRESSION_FAILED,
  VALIDATION_ERROR_CODE,
  ALL_ENTRY_VALIDATION_ERROR_CODE,
]);

type AppliedFilter = {
  searchTerm: string;
  dataQuery: DataQueryPresenterModel;
  skip: number;
  limit: number;
  sort: Sort;
  isObsolete: boolean | undefined;
  state: DataEntryStateEnum | undefined;
};

interface PresentedEntry {
  [key: string]: {
    entity: DataEntityPresenterModel;
    entry: DataEntryPresenterModel;
    canExecuteWorkflowAction?: boolean;
    canTakeOwnership?: boolean;
    attachments?: EntryAttachments[];
  };
}
interface DataEntriesResponseV3 {
  entries?: Array<DataEntry>;
  tags?: Array<{ entriesId?: Array<string>; tagId?: string; groupId?: string }>;
}
export interface EntryStateModel {
  contextTotalCount: Record<string, number>;
  contextDraftTotalCount: Record<string, number>;
  contextPresentedEntries: Record<string, DataEntryPresenterModel[]>;
  contextPresentedDraftEntries: Record<string, DataEntryPresenterModel[]>;
  contextPresentedEntriesTags: Record<string, { [key: string]: string[] }>;
  contextPresentedKanbanEntries: Record<string, KanbanEntriesPresenterModel>;
  contextPresentedDraftKanbanEntries: Record<
    string,
    KanbanEntriesPresenterModel
  >;
  contextPresentedRelationKanbanEntries: Record<
    string,
    KanbanEntriesPresenterModel
  >;
  contextRelationTreeEntries: Record<
    string,
    { [key: string]: DataEntryTreePresenterModel[] }
  >;
  contextRelationTreeState?: Record<string, { [key: string]: boolean }>;
  contextAppliedFilter: Record<string, AppliedFilter>;
  contextRelationEntries?: Record<string, DataEntryPresenterModel[]>;
  contextRelationEntriesTotalCount?: Record<string, number>;
  contextRelationEntriesAppliedFilters?: Record<string, AppliedFilter>;

  // Transitory entries for collection:
  contextUserCollectionEntries?: Record<string, DataEntryPresenterModel[]>;
  contextUserCollectionEntriesTags?: Record<
    string,
    { [key: string]: string[] }
  >;
  contextUserCollectionEntriesTotalCount?: Record<string, number>;
  contextTreeEntries?: Record<
    string,
    { [key: string]: DataEntryTreePresenterModel[] }
  >;

  contextTreeEntriesState?: Record<string, { [key: string]: boolean }>;
  // page: Page<DataEntryPresenterModel>;
  // draftPage: Page<DataEntryPresenterModel>;
  presentedEntry: PresentedEntry;
  presentedEntryEmails: EntryEmailsPresenterModel[];
  presentedEntryDiscussionsAttachments: FileModel[];
  // presentedEntries?: DataEntryPresenterModel[];
  // presentedDraftEntries?: DataEntryPresenterModel[];
  // presentedEntriesTags?: { [key: string]: string[] };
  // presentedKanbanEntries: KanbanEntriesPresenterModel;
  // presentedRelationKanbanEntries: KanbanEntriesPresenterModel;
  // presentedKanbanDraftEntries: KanbanEntriesPresenterModel;

  selectedEntry?: string; // key
  lastCreatedEntry?: string; // key
  lastUpdatedEntry?: string; // key
  loading: LoadingIndicator;
  // appliedFilter: AppliedFilter;
  pendingDataQueryByView: Record<string, DataQueryPresenterModel>;
  workFlowEntryStatus: DataEntryWorkflowStatusPresenterModel;
  selectedEntryHistory: EntryHistoryPresenterModel[];

  // Transitory entries:
  transitoryActiveCount: number;
  transitoryActiveCountForEndUser: number;
  transitoryEntries?: DataEntryPresenterModel[];
  transitoryTreeEntries?: DataEntryTreePresenterModel[];
  transitoryReferenceFieldData?: RefProperty[];
  transitoryEntriesExportFile?: Blob | { success?: boolean };
  // Transitory entries for relation:
  // relationEntries?: DataEntryPresenterModel[];
  // relationEntriesTotalCount?: number;
  // relationEntriesAppliedFilters?: AppliedFilter;

  // Transitory entries for collection:
  // userCollectionEntries?: DataEntryPresenterModel[];
  // userCollectionEntriesTags?: { [key: string]: string[] };
  // userCollectionEntriesTotalCount?: number;

  // Execute workflow errors
  executeWorkFlowErrors?: {
    validationError: boolean;
  };
  // bulk execute workflow result
  bulkExecuteWorkflowResult?: BulkWorkflowActionExecutionResultModel;
  bulkUpgradeResult?: BulkUpgradeResponsePresenterModel;
  bulkUpdateResult?: BulkUpdateResponsePresenterModel;

  // Presented Entry Warnings
  presentedEntryWarnings?: {
    [key: string]: Array<{
      message: LocalizedValueModel;
    }>;
  };
  // Entries Pagination
  entryNavigationInfo?: Record<string, DataEntryNavigationInfoModel>;

  // Inline edit errors
  patchEntryStatus: Record<
    string,
    {
      status: 'error' | 'success';
      error?: string;
    }
  >;

  mappedFieldsValues: { [key: string]: MappingFieldSourcesModel[] };

  groupOwnerUsers?: RefProperty[];
  isPresentedTemplateEntryUsed?: boolean;
}

const initialAppliedFilter = {
  searchTerm: '',
  dataQuery: null,
  skip: 0,
  limit: DEFAULT_PAGESIZE,
  sort: OrderableEntityDefaultSortValue,
  isObsolete: undefined,
  state: undefined,
};
@State<EntryStateModel>({
  name: 'entries',
  defaults: {
    contextAppliedFilter: {},
    contextTotalCount: {},
    contextDraftTotalCount: {},
    contextPresentedEntries: {},
    contextPresentedDraftEntries: {},
    contextPresentedEntriesTags: {},
    contextPresentedKanbanEntries: {},
    contextPresentedDraftKanbanEntries: {},
    contextPresentedRelationKanbanEntries: {},
    contextRelationTreeEntries: {},
    contextRelationEntries: {},
    contextRelationEntriesTotalCount: {},
    contextRelationEntriesAppliedFilters: {},
    contextUserCollectionEntries: {},
    contextUserCollectionEntriesTags: {},
    contextUserCollectionEntriesTotalCount: {},
    contextTreeEntries: {},
    contextTreeEntriesState: {},
    // page: {
    //   items: [],
    //   totalCount: 0,
    // },
    // draftPage: {
    //   items: [],
    //   totalCount: 0,
    // },
    selectedEntry: null,
    // presentedEntries: [],
    presentedEntry: {},
    // presentedKanbanEntries: {},
    // presentedRelationKanbanEntries: {},
    // presentedKanbanDraftEntries: {},
    selectedEntryHistory: [],
    transitoryEntries: [],
    transitoryTreeEntries: [],
    loading: new LoadingIndicator(),

    pendingDataQueryByView: {},

    transitoryActiveCount: 0,
    transitoryActiveCountForEndUser: 0,
    workFlowEntryStatus: {
      workflowId: '',
      isMainFlowDetermined: false,
      steps: null,
    },
    executeWorkFlowErrors: null,
    bulkExecuteWorkflowResult: null,
    bulkUpgradeResult: null,
    bulkUpdateResult: null,
    presentedEntryWarnings: {},
    entryNavigationInfo: {},
    patchEntryStatus: {},
    mappedFieldsValues: {},
    groupOwnerUsers: [],
    presentedEntryEmails: null,
    presentedEntryDiscussionsAttachments: [],
    isPresentedTemplateEntryUsed: false,
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class EntryState {
  constructor(
    private entryDataService: EntryDataService,
    private commentControllerService: CommentControllerService,
    private exportEntryDataService: DataEntryTemplateExportControllerService,
    private apiHelper: ApiUtilsService,
    private dataEntryFactoryService: DataEntryFactoryService,
    private dataEntryHistoryFactoryService: DataEntryHistoryFactoryService,
    private dataEntityFactoryService: EntityFactoryService,
    private dataEntryTreeFactoryService: DataEntryTreeFactoryService,
    private dataViewsService: ViewDataService,
    private dataRelationsService: DataRelationControllerService,
    private reportService: ReportEntryControllerService,
    private dataQueryService: DataQueryService,
    private workflowService: WorkflowDataService,
    private queryFactoryService: QueryFactoryService,
    private collectionService: DataCollectionsControllerService,
    private groupsService: GroupsControllerService,
    private readonly entryWorkFlowStatusFactory: EntryWorkFlowStatusFactoryService,
    private store: Store,
    private fieldFactory: FieldFactoryService,
    private entryEmailFactory: EntryEmailsFactoryService,
    private transformationFactory: TransformationFactoryService,
    private templatesControllerService: TemplatesControllerService,
    private breadcrumbService: BreadcrumbService,
    private typesUtilsService: TypesUtilsService,
  ) {}

  private getKey = EntryState.getKey;
  private getContextKey = EntryState.getContextKey;

  private static getKey(entityId: string, entryId: string): string {
    return `${entityId ?? ''}${entryId ?? ''}`;
  }

  private static getContextKey(entityId: string, contextId?: string): string {
    return `${entityId}-${contextId ?? ''}`;
  }

  private static getEntryPatchKey(entryId: string, fieldUId: number): string {
    return `${entryId}_${fieldUId}`;
  }

  // --------------------------------- SELECTORS ------------------------------

  @Selector([EntryState])
  static selectedEntry(state: EntryStateModel): DataEntryPresenterModel {
    return state.presentedEntry?.[state.selectedEntry]?.entry || null;
  }

  @Selector([EntryState])
  static selectedEntryAttachments(state: EntryStateModel): EntryAttachments[] {
    return state.presentedEntry?.[state.selectedEntry]?.attachments || null;
  }

  @Selector([EntryState])
  static selectedEntryHistory(
    state: EntryStateModel,
  ): EntryHistoryPresenterModel[] {
    return state.selectedEntryHistory || null;
  }

  @Selector([EntryState])
  static presentedEntityForSelectedEntry(
    state: EntryStateModel,
  ): DataEntityPresenterModel {
    return state.presentedEntry?.[state.selectedEntry]?.entity || null;
  }

  static presentedEntry(
    entityId: string,
    entryId: string,
  ): (state: EntryStateModel) => DataEntryPresenterModel {
    return createSelector(
      [EntryState],
      (state: EntryStateModel) =>
        state.presentedEntry[EntryState.getKey(entityId, entryId)]?.entry,
    );
  }

  static canExecuteWorkflowAction(
    entityId: string,
    entryId: string,
  ): (state: EntryStateModel) => boolean {
    return createSelector(
      [EntryState],
      (state: EntryStateModel) =>
        state.presentedEntry[EntryState.getKey(entityId, entryId)]
          .canExecuteWorkflowAction,
    );
  }

  static canTakeOwnership(
    entityId: string,
    entryId: string,
  ): (state: EntryStateModel) => boolean {
    return createSelector(
      [EntryState],
      (state: EntryStateModel) =>
        state.presentedEntry[EntryState.getKey(entityId, entryId)]
          .canTakeOwnership,
    );
  }

  static presentedEntityForEntry(
    entityId: string,
    entryId: string,
  ): (state: EntryStateModel) => DataEntityPresenterModel {
    return createSelector(
      [EntryState],
      (state: EntryStateModel) =>
        state.presentedEntry[EntryState.getKey(entityId, entryId)]?.entity,
    );
  }

  static presentedEntryMappedFieldsValues(
    referenceFieldId: string,
    referenceEntryId: string,
  ): (state: EntryStateModel) => MappingFieldSourcesModel[] {
    return createSelector(
      [EntryState],
      (state: EntryStateModel) =>
        state.mappedFieldsValues[
          EntryState.getKey(referenceFieldId, referenceEntryId)
        ],
    );
  }
  static patchEntryStatusFor(
    entryId: string,
    fieldUId: number,
  ): (state: EntryStateModel) => {
    status: 'success' | 'error';
    error?: string;
  } {
    return createSelector(
      [EntryState],
      (state: EntryStateModel) =>
        state.patchEntryStatus[EntryState.getEntryPatchKey(entryId, fieldUId)],
    );
  }

  static presentedEntries(entityId: string, contextId?: string) {
    return createSelector(
      [EntryState],
      (state: EntryStateModel): DataEntryPresenterModel[] =>
        state.contextPresentedEntries[
          EntryState.getContextKey(entityId, contextId)
        ],
    );
  }

  static presentedKanbanEntries(entityId: string, contextId?: string) {
    return createSelector(
      [EntryState],
      (state: EntryStateModel): KanbanEntriesPresenterModel =>
        state.contextPresentedKanbanEntries[
          EntryState.getContextKey(entityId, contextId)
        ],
    );
  }

  static presentedKanbanDraftEntries(entityId: string, contextId?: string) {
    return createSelector(
      [EntryState],
      (state: EntryStateModel): KanbanEntriesPresenterModel =>
        state.contextPresentedDraftKanbanEntries[
          EntryState.getContextKey(entityId, contextId)
        ],
    );
  }

  static presentedRelationKanbanEntries(entityId: string, contextId?: string) {
    return createSelector(
      [EntryState],
      (state: EntryStateModel): KanbanEntriesPresenterModel =>
        state.contextPresentedRelationKanbanEntries[
          EntryState.getContextKey(entityId, contextId)
        ],
    );
  }

  static presentedDraftEntries(entityId: string, contextId?: string) {
    return createSelector(
      [EntryState],
      (state: EntryStateModel): DataEntryPresenterModel[] =>
        state.contextPresentedDraftEntries[
          EntryState.getContextKey(entityId, contextId)
        ],
    );
  }

  static presentedEntriesTags(entityId: string, contextId?: string) {
    return createSelector(
      [EntryState],
      (
        state: EntryStateModel,
      ): {
        [key: string]: string[];
      } =>
        state.contextPresentedEntriesTags[
          EntryState.getContextKey(entityId, contextId)
        ],
    );
  }

  static userCollectionEntriesTags(entityId: string, contextId?: string) {
    return createSelector(
      [EntryState],
      (
        state: EntryStateModel,
      ): {
        [key: string]: string[];
      } =>
        state.contextUserCollectionEntriesTags[
          EntryState.getContextKey(entityId, contextId)
        ],
    );
  }
  @Selector([EntryState])
  static lastCreatedEntry(state: EntryStateModel): DataEntryPresenterModel {
    return state.presentedEntry[state.lastCreatedEntry]?.entry;
  }

  static totalCount(entityId: string, contextId?: string) {
    return createSelector(
      [EntryState],
      (state: EntryStateModel): number =>
        state.contextTotalCount[EntryState.getContextKey(entityId, contextId)],
    );
  }

  static totalDraftCount(entityId: string, contextId?: string) {
    return createSelector(
      [EntryState],
      (state: EntryStateModel): number =>
        state.contextDraftTotalCount[
          EntryState.getContextKey(entityId, contextId)
        ],
    );
  }

  @Selector()
  /**
   * select transitory entries.
   * it's valid to be readed once after trigger TransitoryFetchEntries action
   */
  static transitoryEntries(state: EntryStateModel): DataEntryPresenterModel[] {
    return state.transitoryEntries;
  }

  @Selector()
  static transitoryTreeEntries(
    state: EntryStateModel,
  ): DataEntryTreePresenterModel[] {
    return state.transitoryTreeEntries;
  }

  static treeEntries(entityId: string, viewId?: string) {
    return createSelector(
      [EntryState],
      (
        state: EntryStateModel,
      ): { [key: string]: DataEntryTreePresenterModel[] } =>
        state.contextTreeEntries[EntryState.getContextKey(entityId, viewId)],
    );
  }

  static treeEntriesState(entityId: string, viewId?: string) {
    return createSelector(
      [EntryState],
      (state: EntryStateModel): { [key: string]: boolean } =>
        state.contextTreeEntriesState[
          EntryState.getContextKey(entityId, viewId)
        ],
    );
  }
  @Selector()
  static transitoryReferenceFieldData(state: EntryStateModel): RefProperty[] {
    return state.transitoryReferenceFieldData;
  }

  static userCollectionEntries(entityId: string, contextId?: string) {
    return createSelector(
      [EntryState],
      (state: EntryStateModel): DataEntryPresenterModel[] =>
        state.contextUserCollectionEntries[
          EntryState.getContextKey(entityId, contextId)
        ],
    );
  }
  static userCollectionEntriesTotalCount(entityId: string, contextId?: string) {
    return createSelector(
      [EntryState],
      (state: EntryStateModel): number =>
        state.contextUserCollectionEntriesTotalCount[
          EntryState.getContextKey(entityId, contextId)
        ],
    );
  }
  @Selector()
  static transitoryActiveCount(state: EntryStateModel): number {
    return state.transitoryActiveCount;
  }

  @Selector()
  static transitoryActiveCountForEndUser(state: EntryStateModel): number {
    return state.transitoryActiveCountForEndUser;
  }

  @Selector()
  static fetchWorkflowStatusEntry(
    state: EntryStateModel,
  ): DataEntryWorkflowStatusPresenterModel {
    return state.workFlowEntryStatus;
  }

  static loading(type: LoadingType) {
    return createSelector(
      [EntryState],
      (state: EntryStateModel) => state.loading[type],
    );
  }

  @Selector([EntryState])
  static loadingIndicator(state: EntryStateModel): LoadingIndicator {
    return state.loading;
  }

  static appliedDataQuery(entityId: string, contextId?: string) {
    return createSelector(
      [EntryState],
      (state: EntryStateModel): DataQueryPresenterModel =>
        state.contextAppliedFilter[
          EntryState.getContextKey(entityId, contextId)
        ]?.dataQuery ?? initialAppliedFilter.dataQuery,
    );
  }

  static pendingDataQueryOfView(viewId: string) {
    return createSelector(
      [EntryState],
      (state: EntryStateModel): DataQueryPresenterModel =>
        state.pendingDataQueryByView[viewId],
    );
  }

  @Selector([EntryState])
  static transitoryActionCount(state: EntryStateModel): number {
    return state.transitoryActiveCount;
  }

  static relationEntries(entityId: string, contextId?: string) {
    return createSelector(
      [EntryState],
      (state: EntryStateModel): DataEntryPresenterModel[] =>
        state.contextRelationEntries[
          EntryState.getContextKey(entityId, contextId)
        ],
    );
  }

  static relationEntriesTotalCount(entityId: string, contextId?: string) {
    return createSelector(
      [EntryState],
      (state: EntryStateModel): number =>
        state.contextRelationEntriesTotalCount[
          EntryState.getContextKey(entityId, contextId)
        ],
    );
  }

  static relationEntriesAppliedFilters(entityId: string, contextId?: string) {
    return createSelector(
      [EntryState],
      (state: EntryStateModel): DataQueryPresenterModel =>
        state.contextRelationEntriesAppliedFilters[
          EntryState.getContextKey(entityId, contextId)
        ]?.dataQuery,
    );
  }

  @Selector([EntryState])
  static executeWorkflowError(state: EntryStateModel): {
    validationError: boolean;
  } {
    return state.executeWorkFlowErrors;
  }

  @Selector([EntryState])
  static bulkExecuteWorkflowResult(state: EntryStateModel) {
    return state.bulkExecuteWorkflowResult;
  }

  @Selector([EntryState])
  static bulkUpgradeResult(state: EntryStateModel) {
    return state.bulkUpgradeResult;
  }

  @Selector([EntryState])
  static bulkUpdateResult(state: EntryStateModel) {
    return state.bulkUpdateResult;
  }

  static presentedEntryWarnings(entityId: string, entryId: string) {
    return createSelector(
      [EntryState],
      (
        state: EntryStateModel,
      ): Array<{
        message: LocalizedValueModel;
      }> => {
        return state?.presentedEntryWarnings?.[this.getKey(entityId, entryId)];
      },
    );
  }

  static entryNavigationInfo(entityId: string, entryId: string) {
    return createSelector(
      [EntryState],
      (state: EntryStateModel): DataEntryNavigationInfoModel =>
        state?.entryNavigationInfo?.[this.getKey(entityId, entryId)],
    );
  }

  @Selector([EntryState])
  static transitoryEntriesExportFile(state: EntryStateModel) {
    return state.transitoryEntriesExportFile;
  }

  @Selector([EntryState])
  static groupOwnerUsers(state: EntryStateModel) {
    return state.groupOwnerUsers;
  }

  @Selector([EntryState])
  static isPresentedTemplateEntryUsed(state: EntryStateModel) {
    return state.isPresentedTemplateEntryUsed;
  }

  static presentedEntryEmails(): (
    state: EntryStateModel,
  ) => EntryEmailsPresenterModel[] {
    return createSelector(
      [EntryState],
      (state: EntryStateModel) => state.presentedEntryEmails,
    );
  }

  static presentedEntryDiscussionsAttachments(): (
    state: EntryStateModel,
  ) => FileModel[] {
    return createSelector(
      [EntryState],
      (state: EntryStateModel) => state.presentedEntryDiscussionsAttachments,
    );
  }
  // --------------------------------- ACTIONS ------------------------------

  /**
   * fetch entries then you can read them from transitory entries selector
   */
  @Action(EntryActions.TransitoryFetchEntries)
  transitoryFetchEntries(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.TransitoryFetchEntries,
  ) {
    let source: Observable<Where<any>>;
    if (payload?.filterCriteria) {
      source = this.dataQueryService.parseExpression(payload?.filterCriteria);
    } else {
      source = of(null);
    }

    return source.pipe(
      switchMap((filterCriteria: Where<any>) => {
        const where: Where<DataEntry> = {
          state: 1,

          ...(!!filterCriteria && filterCriteria),
          id: payload.entryIds?.length
            ? { inq: payload.entryIds }
            : payload?.excludedIds?.length
              ? { nin: payload?.excludedIds }
              : undefined,
          or: this.apiHelper.prepareSearch(
            payload.searchTerm,
            payload.filterBy,
          ),
        };

        const activeCountWhere = this.apiHelper.prepareWhere<DataEntry>({
          id: payload.entryIds ? { inq: payload.entryIds } : undefined,
          state: DataEntryStateEnum.Active,
          ...(!!filterCriteria && filterCriteria),
          or: this.apiHelper.prepareSearch(
            payload.searchTerm,
            payload.filterBy,
          ),
        });

        const filter = this.apiHelper.prepareFilter<DataEntry>({
          where,
          skip: payload.skip ?? 0,
          limit: payload.limit ?? 20,
          order:
            payload.sort && payload.sort?.sortBy && payload.sort?.sortDir
              ? [
                  `${
                    payload.sort.sortBy.startsWith('value')
                      ? payload.sort.sortBy
                      : `value.${payload.sort.sortBy}`
                  } ${payload.sort?.sortDir?.toUpperCase()}`,
                ]
              : undefined,
        });

        return (
          payload.viewId
            ? this.dataViewsService.findEntries({
                entity: payload.entityId,
                id: payload.viewId,
                ...filter,
                context: new ManagedHttpContext({
                  publicAccessId: payload.viewId,
                }),
                appliedQuickFilters: payload.appliedQuickFilters,
              })
            : payload.reportId
              ? this.reportService.executeReport({
                  id: payload.reportId,
                  ...filter,
                })
              : this.entryDataService.find({
                  entity: payload.entityId,
                  ...filter,
                  appliedQuickFilters: payload.appliedQuickFilters,
                })
        ).pipe(
          switchMap((res) => {
            const countSource$ = !payload.withCount
              ? of({ count: 0 })
              : payload.reportId
                ? this.reportService.executeReportCount({
                    id: payload.reportId,
                    where: activeCountWhere,
                  })
                : this.entryDataService.count({
                    entity: payload.entityId,
                    where: activeCountWhere,
                    appliedQuickFilters: payload.appliedQuickFilters,
                  });

            return countSource$.pipe(
              map((count) => ({
                items: res,
                activeCount: count.count,
              })),
            );
          }),
          mergeMap((page) => {
            if (!payload.entityId) {
              return of(page);
            }
            return this._mapPresenterEntries(
              ctx,
              payload.entityId,
              page.items,
              true,
            ).pipe(
              map((mappedItems) => ({
                ...page,
                items: mappedItems,
              })),
            );
          }),
          tap((res) => {
            ctx.patchState({
              transitoryEntries: res.items,
              transitoryActiveCount: res.activeCount,
            });
            /** TODO:
             * new action fetchServiceEntry (servideId)
             * dispatch fetchEntity
             * fetch entry from SERVICE_CATALOG entity.
             * combine entry result with entitystate.presentedEntity and update the presentedEntry based on them.
             * Then we can use SelectEntry action, and presetedEntityForSelectedEntry as normal.
             */
            if (payload.selectable && payload.entityId) {
              const entity = this.store.selectSnapshot(
                EntityState.presentedEntity(payload.entityId),
              );
              ctx.setState(
                patch<EntryStateModel>({
                  presentedEntry: patch<PresentedEntry>({
                    ...res.items.reduce((acc, curr) => {
                      acc[this.getKey(payload.entityId, curr.id)] = {
                        entry: curr,
                        entity,
                      };
                      return acc;
                    }, {}),
                  }),
                }),
              );
            }
          }),
        );
      }),
    );
  }

  @Action(EntryActions.TransitoryFetchReferenceFieldData)
  transitoryFetchReferenceFieldData(
    { patchState }: StateContext<EntryStateModel>,
    { payload }: EntryActions.TransitoryFetchReferenceFieldData,
  ) {
    return this._getFetchEntryCriteriaSource(1, true, false, true).pipe(
      switchMap((where) => {
        const filter = this._prepareFilterOfDataEntry(
          {
            where: {
              ...where,
              id: payload.entryIds?.length
                ? { inq: payload.entryIds }
                : undefined,
              // or: this.apiHelper.prepareSearch(
              //   payload.searchTerm,
              //   payload.filterBy,
              //   payload.mainTitleType,
              // ),
            },
            skip: payload.skip ?? 0,
            limit: payload.limit ?? 20,
            criteria: payload.mappedFieldsFilter?.criteria,
          },
          payload.sort && payload.sort?.sortBy && payload.sort?.sortDir
            ? [payload.sort]
            : [],
        );
        return this.entryDataService.fetchReferenceFieldDataV2({
          field: payload.fieldId,
          entity: payload.entityId,
          body: {
            relatedEntryId: payload.relatedEntryId,
            cascadedParents: payload.cascadedParents,
            context: payload?.field ? 2 : payload.relatedEntryId ? 1 : 0, // Other = 0, Entry = 1
            includeIds: payload?.includeId ?? [],
            filter: JSON.parse(filter.filter),
            field: payload?.field
              ? this.fieldFactory.createDataModel(payload.field)
              : undefined,
            search: payload.searchTerm?.length ? payload.searchTerm : undefined,
            parameters: payload.parameters,
          },
        });
      }),

      tap((res) => {
        patchState({ transitoryReferenceFieldData: res });
      }),
    );
  }

  @Action(EntryActions.FetchUserCollectionEntries)
  fetchCollectionEntries(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.FetchUserCollectionEntries,
  ) {
    ctx.dispatch(new LoadingActions.SetLoading(true));
    const contextKey = this.getContextKey(
      payload.entityId,
      payload.collectionId,
    );
    return this._getFetchEntryCriteriaSource(
      payload.state,
      payload.isValid,
      payload.isObsolete,
      payload.isInvalidRequirement,
    ).pipe(
      switchMap((where) => {
        const totalCountWhere = this.apiHelper.prepareCountWhere<DataEntry>(
          where,
          payload.dataQuery?.criteria,
        );

        const filter = this._prepareFilterOfDataEntry(
          {
            where,
            skip: payload.skip,
            limit: payload.limit,
            criteria: payload.dataQuery?.criteria,
          },
          payload.sort,
        );
        return this._getTransitoryEntriesForCollection({
          entityId: payload.entityId,
          collectionId: payload.collectionId,
          withCount: payload.withCount,
          filter,
          totalCountWhere,
          appliedQuickFilters: payload.appliedQuickFilters,
          appliedTagsFilters: payload.appliedTagsFilters,
          search: payload.searchTerm,
        });
      }),
      switchMap(({ entriesResponse, count }) => {
        return this._mapPresenterEntries(
          ctx,
          payload.entityId,
          entriesResponse.entries,
        ).pipe(
          map((presentedEntries) => ({
            entries: presentedEntries,
            count,
            tags: entriesResponse.tags,
          })),
        );
      }),
      tap(({ count, entries, tags }) => {
        ctx.setState(
          patch<EntryStateModel>({
            contextUserCollectionEntries: patch({ [contextKey]: entries }),
            contextUserCollectionEntriesTags: patch({
              [contextKey]: tags?.reduce(
                (acc, curr) => ({
                  ...acc,
                  [curr.tagId + '_' + curr.groupId]: curr.entriesId,
                }),
                {} as { [key: string]: string[] },
              ),
            }),
            contextUserCollectionEntriesTotalCount: patch({
              [contextKey]: count,
            }),
          }),
        );
      }),
      finalize(() => {
        ctx.dispatch(new LoadingActions.SetLoading(false));
      }),
    );
  }

  @Action(EntryActions.FetchUserCollectionEntriesTags)
  fetchCollectionEntriesTags(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.FetchUserCollectionEntriesTags,
  ) {
    ctx.dispatch(new LoadingActions.SetLoading(true));
    const contextKey = this.getContextKey(
      payload.entityId,
      payload.collectionId,
    );
    return this._getFetchEntryCriteriaSource(
      payload.state,
      payload.isValid,
      payload.isObsolete,
      payload.isInvalidRequirement,
    ).pipe(
      switchMap((where) => {
        const totalCountWhere = this.apiHelper.prepareCountWhere<DataEntry>(
          where,
          payload.dataQuery?.criteria,
        );
        const filter = this._prepareFilterOfDataEntry(
          {
            where,
            skip: payload.skip,
            limit: payload.limit,
            criteria: payload.dataQuery?.criteria,
          },
          payload.sort,
        );
        return this._getTransitoryEntriesForCollection({
          entityId: payload.entityId,
          collectionId: payload.collectionId,
          withCount: false,
          filter,
          totalCountWhere,
          appliedQuickFilters: payload.appliedQuickFilters,
          appliedTagsFilters: payload.appliedTagsFilters,
          search: payload?.searchTerm,
        });
      }),

      tap(({ entriesResponse }) => {
        ctx.setState(
          patch<EntryStateModel>({
            contextUserCollectionEntriesTags: patch({
              [contextKey]: entriesResponse?.tags?.reduce(
                (acc, curr) => ({
                  ...acc,
                  [curr.tagId + '_' + curr.groupId]: curr.entriesId,
                }),
                {} as { [key: string]: string[] },
              ),
            }),
          }),
        );
      }),
      finalize(() => {
        ctx.dispatch(new LoadingActions.SetLoading(false));
      }),
    );
  }

  @Action(EntryActions.FetchRelationEntries)
  fetchRelationEntries(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.FetchRelationEntries,
  ): Observable<any> {
    const contextKey = this.getContextKey(payload.relationId, payload.viewId);

    const relationEntriesAppliedFilters =
      ctx.getState().contextRelationEntriesAppliedFilters[contextKey];
    const appliedFilter = Object.assign({}, relationEntriesAppliedFilters, {
      searchTerm: payload.search,
      dataQuery: payload.dataQuery,
      skip: payload.skip,
      limit: payload.limit,
      sort: payload.sort,
      state: payload?.state,
      isObsolete: payload?.isObsolete,
    });

    ctx.dispatch(new LoadingActions.SetLoading(true));
    ctx.setState(
      patch<EntryStateModel>({
        contextRelationEntries: patch({ [contextKey]: [] }),
      }),
    );

    return this._getFetchEntryCriteriaSource(
      payload.state,
      payload.isValid,
      payload.isObsolete,
      payload.isInvalidRequirement,
    ).pipe(
      switchMap((where) => {
        const totalCountWhere = this.apiHelper.prepareCountWhere<DataEntry>(
          where,
          appliedFilter.dataQuery?.criteria,
        );

        const filter = this._prepareFilterOfDataEntry(
          {
            where,
            skip: appliedFilter.skip,
            limit: appliedFilter.limit,
            criteria: appliedFilter.dataQuery?.criteria,
          },
          appliedFilter.sort,
        );

        return forkJoin({
          result: this.dataRelationsService.findRelatedEntriesV3({
            filterCriteria: filter.filter,
            appliedTagsFilters: payload.appliedTagsFilters,
            entity: payload.entityId,
            entryId: payload.entryId,
            relationId: payload.relationId,
            context: payload.viewId
              ? new ManagedHttpContext({ publicAccessId: payload.viewId })
              : undefined,
            search: appliedFilter.searchTerm,
          }),
          count: this.dataRelationsService.findRelatedEntriesCountV2({
            entity: payload.entityId,
            entryId: payload.entryId,
            relationId: payload.relationId,
            whereCriteria: totalCountWhere,
            context: payload.viewId
              ? new ManagedHttpContext({ publicAccessId: payload.viewId })
              : undefined,
            search: appliedFilter.searchTerm,
          }),
        });
      }),
      mergeMap(({ result, count }) =>
        this._mapPresenterEntries(
          ctx,
          payload.relationEntityId,
          result.entries,
          true,
        ).pipe(
          map((mappedItems) => ({
            result: {
              items: mappedItems,
              totalCount: count.count,
            } satisfies Page<DataEntryPresenterModel>,
            tags: result.tags,
          })),
        ),
      ),
      tap(({ result, tags }) => {
        ctx.setState(
          patch<EntryStateModel>({
            contextRelationEntries: patch({
              [contextKey]: result.items,
            }),
            contextRelationEntriesTotalCount: patch({
              [contextKey]: result.totalCount,
            }),
            contextRelationEntriesAppliedFilters: patch({
              [contextKey]: appliedFilter,
            }),
            contextPresentedEntriesTags: patch({
              [contextKey]: tags?.reduce(
                (acc, curr) => ({
                  ...acc,
                  [curr.tagId + '_' + curr.groupId]: curr.entriesId,
                }),
                {} as { [key: string]: string[] },
              ),
            }),
          }),
        );
      }),
      finalize(() => {
        ctx.dispatch(new LoadingActions.SetLoading(false));
      }),
    );
  }

  @Action(EntryActions.FetchRelationEntriesTags)
  fetchRelationEntriesTags(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.FetchRelationEntriesTags,
  ): Observable<any> {
    const contextKey = this.getContextKey(payload.relationId, payload.viewId);

    const relationEntriesAppliedFilters =
      ctx.getState().contextRelationEntriesAppliedFilters[contextKey];
    const appliedFilter = Object.assign({}, relationEntriesAppliedFilters, {
      searchTerm: payload.search,
      dataQuery: payload.dataQuery,
      skip: payload.skip,
      limit: payload.limit,
      sort: payload.sort,
      state: payload?.state,
      isObsolete: payload?.isObsolete,
    });

    ctx.dispatch(new LoadingActions.SetLoading(true));

    return this._getFetchEntryCriteriaSource(
      payload.state,
      payload.isValid,
      payload.isObsolete,
      payload.isInvalidRequirement,
    ).pipe(
      switchMap((where) => {
        const filter = this._prepareFilterOfDataEntry(
          {
            where,
            skip: appliedFilter.skip,
            limit: appliedFilter.limit,
            criteria: appliedFilter.dataQuery?.criteria,
          },
          appliedFilter.sort,
        );

        return this.dataRelationsService.findRelatedEntriesV3({
          filterCriteria: filter.filter,
          appliedTagsFilters: payload.appliedTagsFilters,
          entity: payload.entityId,
          entryId: payload.entryId,
          relationId: payload.relationId,
          context: payload.viewId
            ? new ManagedHttpContext({ publicAccessId: payload.viewId })
            : undefined,
          search: appliedFilter.searchTerm,
        });
      }),

      tap((result) => {
        ctx.setState(
          patch<EntryStateModel>({
            contextPresentedEntriesTags: patch({
              [contextKey]: result.tags?.reduce(
                (acc, curr) => ({
                  ...acc,
                  [curr.tagId + '_' + curr.groupId]: curr.entriesId,
                }),
                {} as { [key: string]: string[] },
              ),
            }),
          }),
        );
      }),
      finalize(() => {
        ctx.dispatch(new LoadingActions.SetLoading(false));
      }),
    );
  }

  @Action(EntryActions.FetchRelationTreeEntries, { cancelUncompleted: true })
  fetchRelationTreeEntries(
    { setState, dispatch, getState }: StateContext<EntryStateModel>,
    { payload }: EntryActions.FetchRelationTreeEntries,
  ): Observable<any> {
    const contextKey = this.getContextKey(payload.relationId, payload.viewId);
    dispatch(new LoadingActions.SetLoading(true));
    setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          page: true,
        }),
      }),
    );
    const existingItems = getState().contextRelationTreeEntries[contextKey];
    const startingItems = existingItems
      ? Object.keys(existingItems)
      : undefined;

    const criteria = payload.dataQuery?.criteria?.args?.length
      ? payload.dataQuery?.criteria
      : undefined;
    const filter = this._prepareFilterOfDataEntry({
      criteria,
    });

    const hasActiveFilters = !!(
      payload.search ||
      criteria ||
      payload.appliedQuickFilters?.length
    );
    const expandAll = hasActiveFilters;

    // If we have existing items and no active filters, or when expandAll is true, use default behavior (single fetch without startingItems)
    // When expandAll is true, the single fetch already returns all needed data
    const shouldUseSingleFetch = !hasActiveFilters || expandAll;

    return (
      !startingItems?.length || shouldUseSingleFetch
        ? this.entryDataService.findTreeV3({
            entity: payload.entityId,
            field: payload.fieldNormalizedName,
            startingItem:
              shouldUseSingleFetch || expandAll ? undefined : startingItems,
            appliedTagsFilters: payload.appliedTagsFilters,
            search: payload.search,
            appliedQuickFilters: payload.appliedQuickFilters,
            filterCriteria: filter.filter,
            expandAllChildren: expandAll,
            context: payload.viewId
              ? new ManagedHttpContext({ publicAccessId: payload.viewId })
              : undefined,
            relationId: payload.relationId,
            relationEntry: payload.entryId,
          })
        : forkJoin([
            this.entryDataService.findTreeV3({
              entity: payload.entityId,
              field: payload.fieldNormalizedName,
              startingItem: undefined,
              appliedTagsFilters: payload.appliedTagsFilters,
              filterCriteria: filter.filter,
              search: payload.search,
              appliedQuickFilters: payload.appliedQuickFilters,
              expandAllChildren: expandAll,
              context: payload.viewId
                ? new ManagedHttpContext({ publicAccessId: payload.viewId })
                : undefined,
              relationId: payload.relationId,
              relationEntry: payload.entryId,
            }),
            this.entryDataService.findTreeV3({
              entity: payload.entityId,
              field: payload.fieldNormalizedName,
              startingItem: startingItems,
              appliedTagsFilters: payload.appliedTagsFilters,
              filterCriteria: filter.filter,
              search: payload.search,
              appliedQuickFilters: payload.appliedQuickFilters,
              expandAllChildren: expandAll,
              context: payload.viewId
                ? new ManagedHttpContext({ publicAccessId: payload.viewId })
                : undefined,
              relationId: payload.relationId,
              relationEntry: payload.entryId,
            }),
          ]).pipe(
            map(([root, loadedItems]) => {
              const rootTags = root.tags ?? [];
              const itemTags = loadedItems.tags ?? [];
              if (rootTags.length && !itemTags.length) {
                return {
                  entries: root.entries.concat(loadedItems.entries),
                  tags: rootTags,
                };
              }
              if (!rootTags.length && itemTags.length) {
                return {
                  entries: root.entries.concat(loadedItems.entries),
                  tags: itemTags,
                };
              }
              const unionTags: DataEntriesTags[] = [];
              for (const tag of rootTags) {
                const itemTag = itemTags.find((v) => v.tagId === tag.tagId);
                if (itemTag) {
                  unionTags.push({
                    ...tag,
                    entriesId: [...tag.entriesId, ...itemTag.entriesId],
                  });
                } else {
                  unionTags.push(tag);
                }
              }
              for (const tag of itemTags) {
                if (!rootTags.find((v) => v.tagId === tag.tagId)) {
                  unionTags.push(tag);
                }
              }
              return {
                entries: root.entries.concat(loadedItems.entries),
                tags: unionTags,
              };
            }),
          )
    ).pipe(
      switchMap((result) =>
        this.entryDataService
          .count({
            entity: payload.entityId,
            context: payload.viewId
              ? new ManagedHttpContext({ publicAccessId: payload.viewId })
              : undefined,
          })
          .pipe(map((res) => ({ result, count: res.count }))),
      ),
      tap(({ result }) => {
        const presenterModels = result.entries.map((v) =>
          this.dataEntryTreeFactoryService.createPresenterModel(v),
        );

        let contextTreeEntries: {
          [key: string]: DataEntryTreePresenterModel[];
        };
        let expandedState: { [key: string]: boolean } | undefined;

        if (expandAll) {
          // Build tree structure with parent-child relationships
          const treeMap = new Map<string, DataEntryTreePresenterModel[]>();
          const expandedEntries: { [key: string]: boolean } = {};

          // Group entries by their parent ID
          presenterModels.forEach((entry) => {
            const parentField = entry.value[payload.fieldNormalizedName];
            const parentId = parentField?.dataEntry;

            if (parentId) {
              if (!treeMap.has(parentId)) {
                treeMap.set(parentId, []);
              }
              treeMap.get(parentId)!.push(entry);
            }

            // Mark all entries as expanded
            expandedEntries[entry.id] = true;
          });

          // Convert map to the expected format
          contextTreeEntries = Object.fromEntries(treeMap.entries());
          expandedState = expandedEntries;
        }

        setState(
          patch<EntryStateModel>({
            contextRelationEntries: patch({ [contextKey]: presenterModels }),
            contextPresentedEntriesTags: patch({
              [contextKey]: result.tags?.reduce(
                (acc, curr) => ({
                  ...acc,
                  [curr.tagId + '_' + curr.groupId]: curr.entriesId,
                }),
                {} as { [key: string]: string[] },
              ),
            }),
            contextRelationTreeEntries: patch({
              [contextKey]: contextTreeEntries,
            }),
            ...(expandedState && {
              contextTreeEntriesState: patch({
                [contextKey]: expandedState,
              }),
            }),
          }),
        );
      }),
      finalize(() => {
        dispatch(new LoadingActions.SetLoading(false));
        setState(
          patch<EntryStateModel>({
            loading: patch<LoadingIndicator>({
              page: false,
            }),
          }),
        );
      }),
    );
  }

  @Action(EntryActions.FetchRelationKanbanEntries)
  fetchRelationKanbanEntries(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.FetchRelationKanbanEntries,
  ): Observable<any> {
    const contextKey = this.getContextKey(payload.relationId, payload.viewId);

    const relationEntriesAppliedFilters =
      ctx.getState().contextRelationEntriesAppliedFilters[contextKey];

    const appliedFilter = Object.assign({}, relationEntriesAppliedFilters, {
      searchTerm: payload.search,
      dataQuery: payload.dataQuery,
      skip: payload.skip,
      limit: payload.limit,
      sort: payload.sort,
      state: payload?.state,
      isObsolete: payload?.isObsolete,
    });

    ctx.dispatch(new LoadingActions.SetLoading(true));
    const existingKanbanEntries =
      ctx.getState().contextPresentedRelationKanbanEntries[contextKey];
    return this._getFetchEntryCriteriaSource(
      payload.state,
      payload.isValid,
      payload.isObsolete,
      payload.isInvalidRequirement,
    ).pipe(
      switchMap((where) => {
        const totalCountWhere = this.apiHelper.prepareCountWhere<DataEntry>(
          where,
          appliedFilter.dataQuery?.criteria,
        );

        const filter = this._prepareFilterOfDataEntry(
          {
            where,
            skip: appliedFilter.skip,
            limit: appliedFilter.limit,
            criteria: appliedFilter.dataQuery?.criteria,
          },
          appliedFilter.sort,
        );

        return forkJoin({
          result: this.dataRelationsService.findRelatedEntriesKanbanView({
            filterCriteria: filter.filter,
            appliedTagsFilters: payload.appliedTagsFilters,
            entity: payload.entityId,
            entryId: payload.entryId,
            relationId: payload.relationId,
            categoryFieldId: payload.categoryFieldId,
            categoryId: payload.categoryId,
            search: appliedFilter.searchTerm,
            context: payload.viewId
              ? new ManagedHttpContext({ publicAccessId: payload.viewId })
              : undefined,
          }),
          count: this.dataRelationsService.findRelatedEntriesCountV2({
            entity: payload.entityId,
            entryId: payload.entryId,
            relationId: payload.relationId,
            whereCriteria: totalCountWhere,
            search: appliedFilter.searchTerm,
            context: payload.viewId
              ? new ManagedHttpContext({ publicAccessId: payload.viewId })
              : undefined,
          }),
        });
      }),
      mergeMap(({ result, count }) =>
        this._mapPresenterEntriesForKanban(
          ctx,
          payload.relationEntityId,
          result.columns,
          true,
        ).pipe(
          map((mappedItems) => ({
            columns: mappedItems,
            tags: result.tags,
            totalCount: count,
          })),
        ),
      ),
      tap((result) => {
        ctx.setState(
          patch<EntryStateModel>({
            contextRelationEntriesAppliedFilters: patch({
              [contextKey]: appliedFilter,
            }),
            contextRelationEntriesTotalCount: patch({
              [contextKey]: result.totalCount.count,
            }),
            contextPresentedRelationKanbanEntries: patch({
              [contextKey]:
                !!existingKanbanEntries && !!payload.categoryId
                  ? {
                      ...existingKanbanEntries,
                      ...result.columns.reduce(
                        (acc, col) => ({
                          ...acc,
                          [col.categoryId]: {
                            items: existingKanbanEntries?.[col.categoryId]
                              ? [
                                  ...existingKanbanEntries[col.categoryId]
                                    .items,
                                  ...col.items,
                                ]
                              : col.items,
                            totalCount: col.columnCount ?? 0,
                          },
                        }),
                        {},
                      ),
                    }
                  : {
                      ...result.columns.reduce(
                        (acc, col) => ({
                          ...acc,
                          [col.categoryId]: {
                            items: col.items,
                            totalCount: col.columnCount ?? 0,
                          },
                        }),
                        {},
                      ),
                    },
            }),
            contextPresentedEntriesTags: patch({
              [contextKey]: result.tags?.reduce(
                (acc, curr) => ({
                  ...acc,
                  [curr.tagId + '_' + curr.groupId]: curr.entriesId,
                }),
                {} as { [key: string]: string[] },
              ),
            }),
          }),
        );
      }),

      finalize(() => {
        ctx.dispatch(new LoadingActions.SetLoading(false));
      }),
    );
  }

  @Action(EntryActions.FetchRelationEntriesWithoutCount)
  fetchRelationEntriesWithoutCount(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.FetchRelationEntriesWithoutCount,
  ): Observable<any> {
    const contextKey = this.getContextKey(payload.relationId, payload.viewId);

    const relationEntriesAppliedFilters =
      ctx.getState().contextRelationEntriesAppliedFilters[contextKey];

    const appliedFilter = Object.assign({}, relationEntriesAppliedFilters, {
      searchTerm: payload.search,
      dataQuery: payload.dataQuery,
      skip: payload.skip,
      limit: payload.limit,
      sort: payload.sort,
      state: payload?.state,
      isObsolete: payload?.isObsolete,
    });

    ctx.dispatch(new LoadingActions.SetLoading(true));
    ctx.setState(
      patch<EntryStateModel>({
        contextRelationEntries: patch({ [contextKey]: [] }),
      }),
    );

    return this._getFetchEntryCriteriaSource(
      payload.state,
      payload.isValid,
      payload.isObsolete,
      payload.isInvalidRequirement,
    ).pipe(
      switchMap((where) => {
        const filter = this._prepareFilterOfDataEntry(
          {
            where,
            skip: appliedFilter.skip,
            limit: appliedFilter.limit,
            criteria: appliedFilter.dataQuery?.criteria,
          },
          appliedFilter.sort,
        );

        return this.dataRelationsService.findRelatedEntriesV2({
          ...filter,
          entity: payload.entityId,
          entryId: payload.entryId,
          relationId: payload.relationId,
          search: appliedFilter.searchTerm,

          context: payload.viewId
            ? new ManagedHttpContext({ publicAccessId: payload.viewId })
            : undefined,
        });
      }),

      tap((entries) => {
        ctx.setState(
          patch<EntryStateModel>({
            contextRelationEntries: patch({ [contextKey]: entries }),
            contextRelationEntriesAppliedFilters: patch({
              [contextKey]: appliedFilter,
            }),
          }),
        );
      }),
      finalize(() => {
        ctx.dispatch(new LoadingActions.SetLoading(false));
      }),
    );
  }

  @Action(EntryActions.FetchEntries)
  fetchEntries(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.FetchEntries,
  ): Observable<any> {
    const contextKey = EntryState.getContextKey(
      payload.entityId,
      payload.viewId ?? payload.reportId,
    );
    ctx.setState(
      patch<EntryStateModel>({
        contextTotalCount: patch({
          [contextKey]: 0,
        }),
      }),
    );

    const originAppliedFilter =
      ctx.getState().contextAppliedFilter[contextKey] ?? initialAppliedFilter;
    const appliedFilter = Object.assign({}, originAppliedFilter, {
      searchTerm: payload.search,
      dataQuery: payload.dataQuery,
      skip: payload.skip,
      limit: payload.limit,
      sort: payload.sort,
      isObsolete: payload?.isObsolete,
      state: payload?.state,
    });

    const belongToCategoryFilter =
      payload.belongsToCategoryValuesFilter &&
      !!payload.belongsToCategoryValuesFilter?.options?.length
        ? {
            [payload.belongsToCategoryValuesFilter.entryFieldPath]: {
              inq: payload.belongsToCategoryValuesFilter.options,
            },
          }
        : null;
    ctx.dispatch(new LoadingActions.SetLoading(true));

    if (
      payload.showPageLoading === undefined ||
      payload.showPageLoading === true
    ) {
      ctx.setState(
        patch<EntryStateModel>({
          loading: patch<LoadingIndicator>({
            page: true,
          }),
          contextPresentedEntries: patch({
            [contextKey]: [],
          }),
        }),
      );
    }

    return this._getFetchEntryCriteriaSource(
      payload.state,
      payload.isValid,
      payload.isObsolete,
      payload.isInvalidRequirement,
    ).pipe(
      switchMap((where) => {
        where = {
          ...where,
          ...belongToCategoryFilter,
          id: payload.entryIds ? { inq: payload.entryIds } : undefined,
        } satisfies Where<DataEntry>;
        const totalCountWhere = this.apiHelper.prepareCountWhere<DataEntry>(
          where,
          appliedFilter.dataQuery?.criteria,
        );

        const filter = this._prepareFilterOfDataEntry(
          {
            where,
            skip: appliedFilter.skip,
            limit: appliedFilter.limit,
            criteria: appliedFilter.dataQuery?.criteria,
          },
          appliedFilter.sort,
        );

        return (
          payload.viewId
            ? this.dataViewsService
                .findEntriesV3({
                  entity: payload.entityId,
                  id: payload.viewId,
                  filterCriteria: filter.filter,
                  ...(!!payload.filterFirst && {
                    filterFirst: JSON.stringify(payload.filterFirst),
                  }),
                  appliedQuickFilters: payload.appliedQuickFilters,
                  appliedTagsFilters: payload.appliedTagsFilters,
                  search: appliedFilter.searchTerm,
                })
                .pipe(
                  map(
                    (res) =>
                      ({
                        entries: res.entries,
                        tags: res.tags,
                      }) as DataEntriesResponseV3,
                  ),
                )
            : payload.reportId
              ? this.reportService
                  .executeReportV2({
                    id: payload.reportId,
                    filterCriteria: filter.filter,
                    search: appliedFilter.searchTerm,
                  })
                  .pipe(
                    map(
                      (res: any) =>
                        ({
                          entries: res?.entries ?? [],
                          tags: [],
                        }) as DataEntriesResponseV3,
                    ),
                  )
              : this.entryDataService
                  .findV3({
                    entity: payload.entityId,
                    filterCriteria: filter.filter,
                    appliedQuickFilters: payload.appliedQuickFilters,
                    appliedTagsFilters: payload.appliedTagsFilters,
                    search: appliedFilter.searchTerm,
                  })
                  .pipe(
                    map(
                      (res) =>
                        ({
                          entries: res.entries,
                          tags: res.tags,
                        }) as DataEntriesResponseV3,
                    ),
                  )
        ).pipe(
          switchMap((elements) =>
            (payload.viewId
              ? this.dataViewsService.entriesCountV2({
                  entity: payload.entityId,
                  id: payload.viewId,
                  whereCriteria: totalCountWhere,
                  ...(!!payload.filterFirst && {
                    filterFirst: JSON.stringify(payload.filterFirst),
                  }),
                  appliedQuickFilters: payload.appliedQuickFilters,
                  search: appliedFilter.searchTerm,
                })
              : payload.reportId
                ? this.reportService.executeReportCountV2({
                    id: payload.reportId,
                    whereCriteria: totalCountWhere,
                    search: appliedFilter.searchTerm,
                  })
                : this.entryDataService.countV2({
                    entity: payload.entityId,
                    whereCriteria: totalCountWhere,
                    appliedQuickFilters: payload.appliedQuickFilters,
                    search: appliedFilter.searchTerm,
                  })
            ).pipe(
              map((count) => ({
                page: {
                  items: elements.entries,
                  totalCount: count.count,
                } satisfies Page<DataEntry>,
                tags: elements.tags,
              })),
            ),
          ),
          mergeMap((result) => {
            if (payload.entityId) {
              return this._mapPresenterEntries(
                ctx,
                payload.entityId,
                result.page.items,
                payload?.normalizeEntry,
              ).pipe(
                map((mappedItems) => ({
                  ...result.page,
                  items: mappedItems,
                  tags: result.tags,
                })),
              );
            } else {
              return of({
                items: result.page.items,
                totalCount: result.page.totalCount,
                tags: result.tags,
              });
            }
          }),
        );
      }),
      tap((page) => {
        ctx.setState(
          patch<EntryStateModel>({
            contextTotalCount: patch({
              [contextKey]: page.totalCount,
            }),
            contextAppliedFilter: patch({
              [contextKey]: appliedFilter,
            }),
            contextPresentedEntries: patch({
              [contextKey]: page.items,
            }),
            contextPresentedEntriesTags: patch({
              [contextKey]: page.tags?.reduce(
                (acc, curr) => ({
                  ...acc,
                  [curr.tagId + '_' + curr.groupId]: curr.entriesId,
                }),
                {} as { [key: string]: string[] },
              ),
            }),
          }),
        );
      }),
      finalize(() => {
        ctx.dispatch(new LoadingActions.SetLoading(false));
        ctx.setState(
          patch<EntryStateModel>({
            loading: patch<LoadingIndicator>({
              page: false,
            }),
          }),
        );
      }),
    );
  }

  @Action(EntryActions.FetchKanbanEntries)
  fetchKanbanEntries(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.FetchKanbanEntries,
  ): Observable<any> {
    const contextKey = EntryState.getContextKey(
      payload.entityId,
      payload.collectionId ?? payload.viewId,
    );
    const originAppliedFilter =
      ctx.getState().contextAppliedFilter[contextKey] ?? initialAppliedFilter;
    const appliedFilter = Object.assign({}, originAppliedFilter, {
      searchTerm: payload.search,
      dataQuery: payload.dataQuery,
      skip: payload.skip,
      limit: payload.limit,
      sort: payload.sort,
      state: payload?.state,
    });

    const belongToCategoryFilter =
      payload.belongsToCategoryValuesFilter &&
      !!payload.belongsToCategoryValuesFilter?.options?.length
        ? {
            [payload.belongsToCategoryValuesFilter.entryFieldPath]: {
              inq: payload.belongsToCategoryValuesFilter.options,
            },
          }
        : null;
    ctx.dispatch(new LoadingActions.SetLoading(true));
    const existingKanbanEntries =
      ctx.getState().contextPresentedKanbanEntries[contextKey];

    return this._getFetchEntryCriteriaSource(
      payload.state,
      payload.isValid,
      payload.isObsolete,
      payload.isInvalidRequirement,
    ).pipe(
      switchMap((where) => {
        where = {
          ...where,
          ...belongToCategoryFilter,
          id: payload.entryIds ? { inq: payload.entryIds } : undefined,
        } satisfies Where<DataEntry>;
        const totalCountWhere = this.apiHelper.prepareCountWhere<DataEntry>(
          where,
          appliedFilter.dataQuery?.criteria,
        );
        const filter = this._prepareFilterOfDataEntry(
          {
            where,
            skip: appliedFilter.skip,
            limit: appliedFilter.limit,
            criteria: appliedFilter.dataQuery?.criteria,
          },
          appliedFilter.sort,
        );

        return this.entryDataService
          .findKanban({
            entity: payload.entityId,
            filterCriteria: filter.filter,
            appliedQuickFilters: payload.appliedQuickFilters,
            appliedTagsFilters: payload.appliedTagsFilters,
            categoryFieldId: payload.categoryFieldId,
            categoryId: payload.categoryId,
            collectionId: payload.collectionId,
            search: appliedFilter.searchTerm,
            context: payload.viewId
              ? new ManagedHttpContext({ publicAccessId: payload.viewId })
              : undefined,
          })
          .pipe(
            map((res) => ({
              columns: res.columns,

              tags: res.tags,
            })),
          )
          .pipe(
            switchMap((elements) =>
              payload.collectionId
                ? this.collectionService
                    .findEntriesCountV2({
                      entity: payload.entityId,
                      id: payload.collectionId,
                      appliedQuickFilters: payload.appliedQuickFilters,
                      where: totalCountWhere,
                      search: appliedFilter.searchTerm,
                    })
                    .pipe(
                      map((count) => ({
                        columns: elements.columns,
                        tags: elements.tags,
                        totalCount: count.count,
                      })),
                    )
                : this.entryDataService
                    .countV2({
                      entity: payload.entityId,
                      whereCriteria: totalCountWhere,
                      appliedQuickFilters: payload.appliedQuickFilters,
                      search: appliedFilter.searchTerm,
                      context: payload.viewId
                        ? new ManagedHttpContext({
                            publicAccessId: payload.viewId,
                          })
                        : undefined,
                    })
                    .pipe(
                      map((count) => ({
                        columns: elements.columns,
                        tags: elements.tags,
                        totalCount: count.count,
                      })),
                    ),
            ),
            mergeMap((result) => {
              return this._mapPresenterEntriesForKanban(
                ctx,
                payload.entityId,
                result.columns,
                payload?.normalizeEntry,
              ).pipe(
                map((mappedItems) => ({
                  columns: mappedItems,
                  tags: result.tags,
                  totalCount: result.totalCount,
                })),
              );
            }),
          );
      }),
      tap((result) => {
        ctx.setState(
          patch<EntryStateModel>({
            contextTotalCount: patch({
              [contextKey]: result.totalCount,
            }),
            contextAppliedFilter: patch({
              [contextKey]: appliedFilter,
            }),
            contextPresentedKanbanEntries: patch({
              [contextKey]:
                !!existingKanbanEntries && !!payload.categoryId
                  ? {
                      ...existingKanbanEntries,
                      ...result.columns.reduce(
                        (acc, col) => ({
                          ...acc,
                          [col.categoryId]: {
                            items: existingKanbanEntries?.[col.categoryId]
                              ? [
                                  ...existingKanbanEntries[col.categoryId]
                                    .items,
                                  ...col.items,
                                ]
                              : col.items,
                            totalCount: col.columnCount ?? 0,
                          },
                        }),
                        {},
                      ),
                    }
                  : {
                      ...result.columns.reduce(
                        (acc, col) => ({
                          ...acc,
                          [col.categoryId]: {
                            items: col.items,
                            totalCount: col.columnCount ?? 0,
                          },
                        }),
                        {},
                      ),
                    },
            }),
            contextUserCollectionEntriesTotalCount: patch({
              [contextKey]: payload.collectionId ? result.totalCount : null,
            }),

            contextPresentedEntriesTags: patch({
              [contextKey]: result.tags?.reduce(
                (acc, curr) => ({
                  ...acc,
                  [curr.tagId + '_' + curr.groupId]: curr.entriesId,
                }),
                {} as { [key: string]: string[] },
              ),
            }),
          }),
        );
      }),
      finalize(() => {
        ctx.dispatch(new LoadingActions.SetLoading(false));
      }),
    );
  }

  @Action(EntryActions.FetchDraftEntries, { cancelUncompleted: false })
  fetchDraftEntries(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.FetchDraftEntries,
  ): Observable<any> {
    const contextKey = EntryState.getContextKey(
      payload.entityId,
      payload.viewId,
    );
    const originAppliedFilter =
      ctx.getState().contextAppliedFilter[contextKey] ?? initialAppliedFilter;

    const appliedFilter = Object.assign({}, originAppliedFilter, {
      searchTerm: payload.search,
      dataQuery: payload.dataQuery,
      skip: payload.skip,
      limit: payload.limit,
      sort: payload.sort,
    });

    const belongToCategoryFilter =
      payload.belongsToCategoryValuesFilter &&
      !!payload.belongsToCategoryValuesFilter?.options?.length
        ? {
            [payload.belongsToCategoryValuesFilter.entryFieldPath]: {
              inq: payload.belongsToCategoryValuesFilter.options,
            },
          }
        : null;
    ctx.dispatch(new LoadingActions.SetLoading(true));

    if (
      payload.showPageLoading === undefined ||
      payload.showPageLoading === true
    ) {
      ctx.setState(
        patch<EntryStateModel>({
          loading: patch<LoadingIndicator>({
            page: true,
          }),
          contextPresentedDraftEntries: patch({
            [contextKey]: [],
          }),
        }),
      );
    }

    return this._getFetchEntryCriteriaSource(undefined, true, true, true).pipe(
      switchMap((where) => {
        where = {
          ...where,
          ...belongToCategoryFilter,
          id: payload.entryIds ? { inq: payload.entryIds } : undefined,
        } satisfies Where<DataEntry>;
        const totalCountWhere = this.apiHelper.prepareCountWhere<DataEntry>(
          where,
          appliedFilter.dataQuery?.criteria,
        );

        const filter = this._prepareFilterOfDataEntry(
          {
            where,
            skip: appliedFilter.skip,
            limit: appliedFilter.limit,
            criteria: appliedFilter.dataQuery?.criteria,
          },
          appliedFilter.sort,
        );

        return this.entryDataService
          .findDraftEntries({
            entity: payload.entityId,
            filterCriteria: filter.filter,
            search: appliedFilter.searchTerm,

            context: payload.viewId
              ? new ManagedHttpContext({ publicAccessId: payload.viewId })
              : undefined,
          })
          .pipe(
            map(
              (res) =>
                ({
                  entries: res.entries,
                  tags: res.tags,
                }) as DataEntriesResponseV3,
            ),
          )
          .pipe(
            switchMap((elements) =>
              this.entryDataService
                .countDraftEntries({
                  entity: payload.entityId,
                  filterCriteria: totalCountWhere,
                  search: appliedFilter.searchTerm,
                })
                .pipe(
                  map((count) => ({
                    page: {
                      items: elements.entries,
                      totalCount: count.count,
                    } satisfies Page<DataEntry>,
                    tags: elements.tags,
                  })),
                ),
            ),
            mergeMap((result) => {
              return this._mapPresenterEntries(
                ctx,
                payload.entityId,
                result.page.items,
                payload?.normalizeEntry,
              ).pipe(
                map((mappedItems) => ({
                  ...result.page,
                  items: mappedItems,
                  tags: result.tags,
                })),
              );
            }),
          );
      }),
      tap((page) => {
        ctx.setState(
          patch<EntryStateModel>({
            contextDraftTotalCount: patch({ [contextKey]: page.totalCount }),
            contextAppliedFilter: patch({ [contextKey]: appliedFilter }),
            contextPresentedDraftEntries: patch({ [contextKey]: page.items }),
          }),
        );
      }),
      finalize(() => {
        ctx.dispatch(new LoadingActions.SetLoading(false));
        ctx.setState(
          patch<EntryStateModel>({
            loading: patch<LoadingIndicator>({
              page: false,
            }),
          }),
        );
      }),
    );
  }

  @Action(EntryActions.CountDraftEntries)
  countDraftEntries(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.CountDraftEntries,
  ): Observable<any> {
    const contextKey = EntryState.getContextKey(
      payload.entityId,
      payload.viewId,
    );
    const originAppliedFilter =
      ctx.getState().contextAppliedFilter[contextKey] ?? initialAppliedFilter;

    const appliedFilter = Object.assign({}, originAppliedFilter, {
      searchTerm: payload.search,
      dataQuery: payload.dataQuery,
      skip: payload.skip,
      limit: payload.limit,
      sort: payload.sort,
    });

    const belongToCategoryFilter =
      payload.belongsToCategoryValuesFilter &&
      !!payload.belongsToCategoryValuesFilter?.options?.length
        ? {
            [payload.belongsToCategoryValuesFilter.entryFieldPath]: {
              inq: payload.belongsToCategoryValuesFilter.options,
            },
          }
        : null;

    return this._getFetchEntryCriteriaSource(undefined, true, true, true).pipe(
      switchMap((where) => {
        where = {
          ...where,
          ...belongToCategoryFilter,
          id: payload.entryIds ? { inq: payload.entryIds } : undefined,
        } satisfies Where<DataEntry>;
        const totalCountWhere = this.apiHelper.prepareCountWhere<DataEntry>(
          where,
          appliedFilter.dataQuery?.criteria,
        );

        return this.entryDataService.countDraftEntries({
          entity: payload.entityId,
          filterCriteria: totalCountWhere,
          search: appliedFilter.searchTerm,
        });
      }),

      tap((count) => {
        ctx.setState(
          patch<EntryStateModel>({
            contextDraftTotalCount: patch({ [contextKey]: count.count }),
          }),
        );
      }),
    );
  }

  @Action(EntryActions.FetchKanbanDraftEntries, { cancelUncompleted: false })
  fetchKanbanDraftEntries(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.FetchKanbanDraftEntries,
  ): Observable<any> {
    const contextKey = EntryState.getContextKey(
      payload.entityId,
      payload.viewId,
    );
    const originAppliedFilter =
      ctx.getState().contextAppliedFilter[contextKey] ?? initialAppliedFilter;

    const appliedFilter = Object.assign({}, originAppliedFilter, {
      searchTerm: payload.search,
      dataQuery: payload.dataQuery,
      skip: payload.skip,
      limit: payload.limit,
      sort: payload.sort,
    });

    const belongToCategoryFilter =
      payload.belongsToCategoryValuesFilter &&
      !!payload.belongsToCategoryValuesFilter?.options?.length
        ? {
            [payload.belongsToCategoryValuesFilter.entryFieldPath]: {
              inq: payload.belongsToCategoryValuesFilter.options,
            },
          }
        : null;
    ctx.dispatch(new LoadingActions.SetLoading(true));

    const existingKanbanDraftEntries =
      ctx.getState().contextPresentedDraftKanbanEntries[contextKey];

    return this._getFetchEntryCriteriaSource(undefined, true, true, true).pipe(
      switchMap((where) => {
        where = {
          ...where,
          ...belongToCategoryFilter,
          id: payload.entryIds ? { inq: payload.entryIds } : undefined,
        } satisfies Where<DataEntry>;
        const totalCountWhere = this.apiHelper.prepareCountWhere<DataEntry>(
          where,
          appliedFilter.dataQuery?.criteria,
        );

        const filter = this._prepareFilterOfDataEntry(
          {
            where,
            skip: appliedFilter.skip,
            limit: appliedFilter.limit,
            criteria: appliedFilter.dataQuery?.criteria,
          },
          appliedFilter.sort,
        );

        return this.entryDataService
          .findKanbanDraft({
            entity: payload.entityId,
            filterCriteria: filter.filter,
            context: payload.viewId
              ? new ManagedHttpContext({ publicAccessId: payload.viewId })
              : undefined,
            categoryFieldId: payload.categoryFieldId,
            categoryId: payload.categoryId,
            search: appliedFilter.searchTerm,
          })
          .pipe(
            map((res) => ({
              columns: res.columns,

              tags: res.tags,
            })),
          )
          .pipe(
            switchMap((elements) =>
              this.entryDataService
                .countDraftEntries({
                  entity: payload.entityId,
                  filterCriteria: totalCountWhere,
                  search: appliedFilter.searchTerm,
                })
                .pipe(
                  map((count) => ({
                    columns: elements.columns,
                    tags: elements.tags,
                    totalCount: count.count,
                  })),
                ),
            ),
            mergeMap((result) => {
              return this._mapPresenterEntriesForKanban(
                ctx,
                payload.entityId,
                result.columns,
                payload?.normalizeEntry,
              ).pipe(
                map((mappedItems) => ({
                  columns: mappedItems,
                  tags: result.tags,
                  totalCount: result.totalCount ?? 0,
                })),
              );
            }),
          );
      }),
      tap((result) => {
        ctx.setState(
          patch<EntryStateModel>({
            contextPresentedDraftKanbanEntries: patch({
              [contextKey]:
                !!existingKanbanDraftEntries && !!payload.categoryId
                  ? {
                      ...existingKanbanDraftEntries,
                      ...result.columns.reduce(
                        (acc, col) => ({
                          ...acc,
                          [col.categoryId]: {
                            items: existingKanbanDraftEntries?.[col.categoryId]
                              ? [
                                  ...existingKanbanDraftEntries[col.categoryId]
                                    .items,
                                  ...col.items,
                                ]
                              : col.items,
                            totalCount: col.columnCount,
                          },
                        }),
                        {},
                      ),
                    }
                  : {
                      ...result.columns.reduce(
                        (acc, col) => ({
                          ...acc,
                          [col.categoryId]: {
                            items: col.items,
                            totalCount: col.columnCount,
                          },
                        }),
                        {},
                      ),
                    },
            }),
          }),
        );
      }),
      finalize(() => {
        ctx.dispatch(new LoadingActions.SetLoading(false));
      }),
    );
  }

  @Action(EntryActions.FetchEntriesTags)
  fetchEntriesTags(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.FetchEntriesTags,
  ): Observable<any> {
    const contextKey = EntryState.getContextKey(
      payload.entityId,
      payload.viewId,
    );
    const originAppliedFilter =
      ctx.getState().contextAppliedFilter[contextKey] ?? initialAppliedFilter;

    const appliedFilter = Object.assign({}, originAppliedFilter, {
      searchTerm: payload.search,
      dataQuery: payload.dataQuery,
      skip: payload.skip,
      limit: payload.limit,
      sort: payload.sort,
      isObsolete: payload?.isObsolete,
      state: payload?.state,
    });

    const belongToCategoryFilter =
      payload.belongsToCategoryValuesFilter &&
      !!payload.belongsToCategoryValuesFilter?.options?.length
        ? {
            [payload.belongsToCategoryValuesFilter.entryFieldPath]: {
              inq: payload.belongsToCategoryValuesFilter.options,
            },
          }
        : null;
    ctx.dispatch(new LoadingActions.SetLoading(true));

    return this._getFetchEntryCriteriaSource(
      payload.state,
      payload.isValid,
      payload.isObsolete,
      payload.isInvalidRequirement,
    )
      .pipe(
        switchMap((where) => {
          where = {
            ...where,
            ...belongToCategoryFilter,
            id: payload.entryIds ? { inq: payload.entryIds } : undefined,
          } satisfies Where<DataEntry>;

          const filter = this._prepareFilterOfDataEntry(
            {
              where,
              skip: appliedFilter.skip,
              limit: appliedFilter.limit,
              criteria: appliedFilter.dataQuery?.criteria,
            },
            appliedFilter.sort,
          );

          return payload.viewId
            ? this.dataViewsService
                .findEntriesV3({
                  entity: payload.entityId,
                  id: payload.viewId,
                  filterCriteria: filter.filter,
                  ...(!!payload.filterFirst && {
                    filterFirst: JSON.stringify(payload.filterFirst),
                  }),
                  appliedQuickFilters: payload.appliedQuickFilters,
                  appliedTagsFilters: payload.appliedTagsFilters,
                  search: appliedFilter.searchTerm,
                })
                .pipe(
                  map(
                    (res) =>
                      ({
                        entries: res.entries,
                        tags: res.tags,
                      }) as DataEntriesResponseV3,
                  ),
                )
            : this.entryDataService
                .findV3({
                  entity: payload.entityId,
                  filterCriteria: filter.filter,
                  appliedQuickFilters: payload.appliedQuickFilters,
                  appliedTagsFilters: payload.appliedTagsFilters,
                  search: appliedFilter.searchTerm,
                })
                .pipe(
                  map(
                    (res) =>
                      ({
                        entries: res.entries,
                        tags: res.tags,
                      }) as DataEntriesResponseV3,
                  ),
                );
        }),
      )
      .pipe(
        tap(({ tags }) => {
          ctx.setState(
            patch<EntryStateModel>({
              contextPresentedEntriesTags: patch({
                [contextKey]: tags?.reduce(
                  (acc, curr) => ({
                    ...acc,
                    [curr.tagId + '_' + curr.groupId]: curr.entriesId,
                  }),
                  {} as { [key: string]: string[] },
                ),
              }),
            }),
          );
        }),
        finalize(() => {
          ctx.dispatch(new LoadingActions.SetLoading(false));
        }),
      );
  }

  @Action(EntryActions.FetchTreeEntries, { cancelUncompleted: true })
  fetchTreeEntries(
    { setState, dispatch, getState }: StateContext<EntryStateModel>,
    { payload }: EntryActions.FetchTreeEntries,
  ): Observable<any> {
    const contextKey = EntryState.getContextKey(
      payload.entityId,
      payload.viewId,
    );
    dispatch(new LoadingActions.SetLoading(true));
    setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          page: true,
        }),
      }),
    );
    const existingItems = getState().contextTreeEntries[contextKey];

    const startingItems = existingItems
      ? Object.keys(existingItems)
      : undefined;

    const criteria = payload.dataQuery?.criteria?.args?.length
      ? payload.dataQuery?.criteria
      : undefined;
    const filter = this._prepareFilterOfDataEntry({
      criteria,
    });

    const hasActiveFilters = !!(
      payload.search ||
      criteria ||
      payload.appliedQuickFilters?.length
    );
    const expandAll = hasActiveFilters;

    // If we have existing items and no active filters, or when expandAll is true, use default behavior (single fetch without startingItems)
    // When expandAll is true, the single fetch already returns all needed data
    const shouldUseSingleFetch = !hasActiveFilters || expandAll;

    return (
      !startingItems?.length || shouldUseSingleFetch
        ? this.entryDataService.findTreeV3({
            entity: payload.entityId,
            field: payload.fieldSource,
            startingItem:
              shouldUseSingleFetch || expandAll ? undefined : startingItems,
            appliedTagsFilters: payload.appliedTagsFilters,
            search: payload.search,
            appliedQuickFilters: payload.appliedQuickFilters,
            filterCriteria: filter.filter,
            expandAllChildren: expandAll,
            context: payload.viewId
              ? new ManagedHttpContext({ publicAccessId: payload.viewId })
              : undefined,
          })
        : forkJoin([
            this.entryDataService.findTreeV3({
              entity: payload.entityId,
              field: payload.fieldSource,
              startingItem: undefined,
              appliedTagsFilters: payload.appliedTagsFilters,
              filterCriteria: filter.filter,
              search: payload.search,
              appliedQuickFilters: payload.appliedQuickFilters,
              expandAllChildren: expandAll,
              context: payload.viewId
                ? new ManagedHttpContext({ publicAccessId: payload.viewId })
                : undefined,
            }),
            this.entryDataService.findTreeV3({
              entity: payload.entityId,
              field: payload.fieldSource,
              startingItem: startingItems,
              appliedTagsFilters: payload.appliedTagsFilters,
              filterCriteria: filter.filter,
              search: payload.search,
              appliedQuickFilters: payload.appliedQuickFilters,
              expandAllChildren: expandAll,
              context: payload.viewId
                ? new ManagedHttpContext({ publicAccessId: payload.viewId })
                : undefined,
            }),
          ]).pipe(
            map(([root, loadedItems]) => {
              const rootTags = root.tags ?? [];
              const itemTags = loadedItems.tags ?? [];
              if (rootTags.length && !itemTags.length) {
                return {
                  entries: root.entries.concat(loadedItems.entries),
                  tags: rootTags,
                };
              }
              if (!rootTags.length && itemTags.length) {
                return {
                  entries: root.entries.concat(loadedItems.entries),
                  tags: itemTags,
                };
              }
              const unionTags: DataEntriesTags[] = [];
              for (const tag of rootTags) {
                const itemTag = itemTags.find((v) => v.tagId === tag.tagId);
                if (itemTag) {
                  unionTags.push({
                    ...tag,
                    entriesId: [...tag.entriesId, ...itemTag.entriesId],
                  });
                } else {
                  unionTags.push(tag);
                }
              }
              for (const tag of itemTags) {
                if (!rootTags.find((v) => v.tagId === tag.tagId)) {
                  unionTags.push(tag);
                }
              }
              return {
                entries: root.entries.concat(loadedItems.entries),
                tags: unionTags,
              };
            }),
          )
    ).pipe(
      switchMap((result) =>
        this.entryDataService
          .count({
            entity: payload.entityId,
            context: payload.viewId
              ? new ManagedHttpContext({ publicAccessId: payload.viewId })
              : undefined,
          })
          .pipe(map((res) => ({ result, count: res.count }))),
      ),
      tap(({ result }) => {
        const presenterModels = result.entries.map((v) =>
          this.dataEntryTreeFactoryService.createPresenterModel(v),
        );

        let contextTreeEntries: {
          [key: string]: DataEntryTreePresenterModel[];
        };
        let expandedState: { [key: string]: boolean } | undefined;

        if (expandAll) {
          // Build tree structure with parent-child relationships
          const treeMap = new Map<string, DataEntryTreePresenterModel[]>();
          const expandedEntries: { [key: string]: boolean } = {};

          // Group entries by their parent ID
          presenterModels.forEach((entry) => {
            const parentField = entry.value[payload.fieldNormalizedName];
            const parentId = parentField?.dataEntry;

            if (parentId) {
              if (!treeMap.has(parentId)) {
                treeMap.set(parentId, []);
              }
              treeMap.get(parentId)!.push(entry);
            }

            // Mark all entries as expanded
            expandedEntries[entry.id] = true;
          });

          // Convert map to the expected format
          contextTreeEntries = Object.fromEntries(treeMap.entries());
          expandedState = expandedEntries;
        }

        setState(
          patch<EntryStateModel>({
            contextPresentedEntries: patch({ [contextKey]: presenterModels }),
            contextPresentedEntriesTags: patch({
              [contextKey]: result.tags?.reduce(
                (acc, curr) => ({
                  ...acc,
                  [curr.tagId + '_' + curr.groupId]: curr.entriesId,
                }),
                {} as { [key: string]: string[] },
              ),
            }),
            contextTreeEntries: patch({
              [contextKey]: contextTreeEntries,
            }),
            ...(expandedState && {
              contextTreeEntriesState: patch({
                [contextKey]: expandedState,
              }),
            }),
          }),
        );
      }),
      finalize(() => {
        dispatch(new LoadingActions.SetLoading(false));
        setState(
          patch<EntryStateModel>({
            loading: patch<LoadingIndicator>({
              page: false,
            }),
          }),
        );
      }),
    );
  }

  @Action(EntryActions.FetchTransitoryTreeEntries, {
    cancelUncompleted: true,
  })
  fetchTreeEntriesByStartingItem(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.FetchTransitoryTreeEntries,
  ): Observable<any> {
    const contextKey = EntryState.getContextKey(
      payload.entityId,
      payload.viewId,
    );

    const filter = this._prepareFilterOfDataEntry({
      criteria: payload.dataQuery?.criteria,
    });
    return this.entryDataService
      .findTreeV3({
        entity: payload.entityId,
        startingItem: [payload.startingItem],
        field: payload.fieldSource,
        appliedTagsFilters: payload.appliedTagsFilters,
        expandAllChildren: payload.expandAll,
        filterCriteria: filter.filter,
        search: payload.search,
        appliedQuickFilters: payload.appliedQuickFilters,
        relationId: payload.relationId,
        relationEntry: payload.relatedEntryId,
      })
      .pipe(
        tap((treeItems) => {
          const state = ctx.getState();
          const existingTags = state.contextPresentedEntriesTags[contextKey];
          const newTags = this.getNewTags(existingTags, treeItems.tags);
          const entries = treeItems.entries.map((item) =>
            this.dataEntryTreeFactoryService.createPresenterModel(item),
          );
          const existingTreeEntries = state.contextTreeEntries[contextKey];
          const existingTreeEntriesState =
            state.contextTreeEntriesState[contextKey];
          ctx.setState(
            patch<EntryStateModel>({
              contextTreeEntries: patch({
                [contextKey]: existingTreeEntries
                  ? patch({ [payload.startingItem]: entries })
                  : {
                      [payload.startingItem]: entries,
                    },
              }),
              contextTreeEntriesState: patch({
                [contextKey]: existingTreeEntriesState
                  ? patch({
                      [payload.startingItem]: true,
                    })
                  : {
                      [payload.startingItem]: true,
                    },
              }),
              transitoryTreeEntries: entries,
              contextPresentedEntriesTags: patch({ [contextKey]: newTags }),
            }),
          );
        }),
      );
  }

  getNewTags(existingTags: any, newTags: any) {
    const newUpdatedTags = newTags?.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.tagId + '_' + curr.groupId]: curr.entriesId,
      }),
      {} as { [key: string]: string[] },
    );
    const unionTags = { ...existingTags };
    for (const key in newUpdatedTags) {
      if (unionTags.hasOwnProperty(key)) {
        unionTags[key] = [...unionTags[key], ...newUpdatedTags[key]];
      } else {
        unionTags[key] = newUpdatedTags[key];
      }
    }
    return unionTags;
  }

  @Action(EntryActions.SetTreeEntryState, {
    cancelUncompleted: true,
  })
  setTreeEntryState(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.SetTreeEntryState,
  ): void {
    const contextKey = EntryState.getContextKey(
      payload.entityId,
      payload.viewId,
    );
    const existingTreeEntriesState =
      ctx.getState().contextTreeEntriesState[contextKey];
    ctx.setState(
      patch<EntryStateModel>({
        contextTreeEntriesState: patch({
          [contextKey]: existingTreeEntriesState
            ? patch({
                [payload.startingItem]: payload.state,
              })
            : {
                [payload.startingItem]: payload.state,
              },
        }),
      }),
    );
  }

  @Action(EntryActions.SelectEntry)
  selectEntry(
    ctx: StateContext<EntryStateModel>,
    { entityId, entryId }: EntryActions.SelectEntry,
  ): Observable<any> | null {
    const selectedEntryKey = this.getKey(entityId, entryId);

    ctx.patchState({
      selectedEntry: selectedEntryKey,
      selectedEntryHistory: [],
    });

    if (!entityId) {
      return null;
    }
    // Select the entry's data entity as the current entity:
    return ctx.dispatch(new EntityActions.FetchEntity(entityId)).pipe(
      tap(() =>
        ctx.dispatch(
          new EntityActions.SelectEntity({
            entityId,
            resolveEntryBreadcrumbTokens: true,
            preservedEntries: [entryId],
          }),
        ),
      ),
    );
  }

  @Action(EntryActions.FetchEntry)
  fetchEntry(
    { setState }: StateContext<EntryStateModel>,
    {
      entityId,
      entryId,
      viewId,
      navigationOptions,
      isDraft,
    }: EntryActions.FetchEntry,
  ) {
    if (!navigationOptions) {
      navigationOptions = {
        includeNavigationLinks: false,
      };
    }
    const { includeNavigationLinks, parentId, relationId } = navigationOptions;

    // Prepare the filter for the entry if navigation links are required:
    const fetchEntryFilter$ = includeNavigationLinks
      ? this._getFetchEntryCriteriaSource(
          navigationOptions.entryState,
          true,
          navigationOptions.entryObsolete,
          true,
        ).pipe(
          map(
            (where) =>
              this._prepareFilterOfDataEntry(
                { where, criteria: navigationOptions.dataQuery?.criteria },
                navigationOptions.sort,
              ).filter,
          ),
        )
      : of(undefined);

    return fetchEntryFilter$.pipe(
      switchMap((filter) =>
        isDraft && !!entryId
          ? this.entryDataService.findEntityAndDraftEntryById({
              entity: entityId,
              id: entryId,

              context: viewId
                ? new ManagedHttpContext({ publicAccessId: viewId })
                : undefined,
            })
          : this.entryDataService.findEntityAndEntryById({
              entity: entityId,
              id: entryId || 'new',
              parameters: navigationOptions.filtersFist
                ? JSON.stringify(navigationOptions.filtersFist)
                : undefined,

              context: viewId
                ? new ManagedHttpContext({ publicAccessId: viewId })
                : undefined,

              includeNavigationLinks: includeNavigationLinks || undefined,
              parentId: parentId || undefined,
              relationId: relationId || undefined,
              collectionId: navigationOptions?.collectionId || undefined,
              filter,
              search: navigationOptions.searchTerm,
            }),
      ),
      tap(
        ({
          entity,
          entry,
          next,
          previous,
          warnings,
          currentIndex,
          totalCount,
          canExecuteWorkflowAction,
          canTakeOwnership,
          attachments,
        }) => {
          const { presentedEntity, presentedEntry } =
            this._mapPresenterEntryAndEntity(entity, entry, !isDraft);
          const mainTitle = getMainTitleFieldValue(
            presentedEntity,
            presentedEntry,
            this.typesUtilsService,
          );
          this.breadcrumbService.set(BreadcrumbToken.EntryTitle, mainTitle);
          setState(
            patch<EntryStateModel>({
              presentedEntry: patch<PresentedEntry>({
                [this.getKey(entityId, entryId)]: {
                  entity: presentedEntity,
                  entry: presentedEntry,
                  canExecuteWorkflowAction: canExecuteWorkflowAction,
                  canTakeOwnership: canTakeOwnership,
                  attachments: attachments,
                },
              }),
              entryNavigationInfo: patch({
                [this.getKey(entityId, entryId)]: includeNavigationLinks
                  ? {
                      index: currentIndex,
                      previousId: previous,
                      nextId: next,
                      totalCount,
                    }
                  : undefined,
              }),
              presentedEntryWarnings: patch({
                [this.getKey(entityId, entryId)]: warnings?.length
                  ? warnings.map((warning) => ({
                      message: warning.errorMessage,
                    }))
                  : null,
              }),
            }),
          );
        },
      ),
    );
  }

  @Action(EntryActions.FetchWorkflowStatusEntry)
  fetchWorkflowStatusEntry(
    { setState }: StateContext<EntryStateModel>,
    { entityId, entryId, mode }: EntryActions.FetchWorkflowStatusEntry,
  ) {
    return this.workflowService
      .getMainWorkflowStatusV2({
        entity: entityId,
        entryId: entryId,
        mode,
      })
      .pipe(
        tap((response: any) => {
          const presenterWorkFlowStatus =
            this.entryWorkFlowStatusFactory.createPresenterModel(response);

          setState(
            patch<EntryStateModel>({
              workFlowEntryStatus: patch(presenterWorkFlowStatus),
            }),
          );
        }),
      );
  }

  @Action(EntryActions.CreateEntry)
  createEntry(
    { setState, dispatch }: StateContext<EntryStateModel>,
    {
      entityId,
      entry,
      viewId,
      selectEntry,
      refValues,
      originalDraftId,
      systemButtonId,
      saveAsInvalid,
    }: EntryActions.CreateEntry,
  ): Observable<DataEntry> {
    dispatch(new LoadingActions.SetCommandLoading(true));

    setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          create: true,
        }),
      }),
    );
    return this.entryDataService
      .create({
        entity: entityId,
        body: {
          ...this.dataEntryFactoryService.createDataModel(entry),
          originalDraftId: originalDraftId,
          referencedValues: refValues,
          systemButtonId,
          saveAsInvalid,
        },
        ...(!!viewId && {
          context: new ManagedHttpContext({ publicAccessId: viewId }),
        }),
      })
      .pipe(
        tap((res) => {
          const presentedEntry =
            this.dataEntryFactoryService.createPresenterModel(res);

          //TODO: don't handle the presentedEntity here. instead, just dispatch the FetchEntry action
          const presentedEntryKey = this.getKey(entityId, presentedEntry.id);
          setState(
            patch<EntryStateModel>({
              presentedEntry: patch<PresentedEntry>({
                [presentedEntryKey]: {
                  entry: presentedEntry,
                  entity: this.store.selectSnapshot(
                    EntryState.presentedEntityForEntry(entityId, null),
                  ),
                },
              }),
              lastCreatedEntry: presentedEntryKey,
            }),
          );
          dispatch(
            new DashboardActions.ClearPresentedStatisticByEntityId(entityId),
          );
          if (selectEntry) {
            dispatch(new EntryActions.SelectEntry(res.dataEntity, res.id));
          }
        }),
        finalize(() => {
          dispatch(new CoreCountersActions.FetchCountersEntriesCount());
          dispatch(new LoadingActions.SetCommandLoading(false));
          if (entityId === SPACE_USERS_CORE_ENTITY_ID) {
            dispatch(new CoreSpaceActions.MySpaces());
          }

          setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                create: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntryActions.QuickCreateEntry)
  quickCreateEntry(
    ctx: StateContext<EntryStateModel>,
    { entityId, entry, viewId, saveAsInvalid }: EntryActions.QuickCreateEntry,
  ): Observable<DataEntry> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(entityId, viewId);

    ctx.setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          create: true,
        }),
      }),
    );
    return this.entryDataService
      .create({
        entity: entityId,
        body: {
          ...this.dataEntryFactoryService.createDataModel(entry),

          saveAsInvalid,
        },
        ...(!!viewId && {
          context: new ManagedHttpContext({ publicAccessId: viewId }),
        }),
      })
      .pipe(
        switchMap((res) =>
          this._fetchEntryAndUpdateState(ctx, entityId, contextKey, res.id),
        ),
        tap(() => {
          ctx.dispatch(
            new DashboardActions.ClearPresentedStatisticByEntityId(entityId),
          );
        }),
        finalize(() => {
          ctx.dispatch(new CoreCountersActions.FetchCountersEntriesCount());
          ctx.dispatch(new LoadingActions.SetCommandLoading(false));

          ctx.setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                create: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntryActions.QuickCreateRelatedEntry)
  quickCreateRelatedEntry(
    ctx: StateContext<EntryStateModel>,
    {
      entityId,
      entry,
      relationId,
      viewId,
      saveAsInvalid,
    }: EntryActions.QuickCreateRelatedEntry,
  ): Observable<DataEntry> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(relationId, viewId);

    ctx.setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          create: true,
        }),
      }),
    );
    return this.entryDataService
      .create({
        entity: entityId,
        body: {
          ...this.dataEntryFactoryService.createDataModel(entry),

          saveAsInvalid,
        },
        ...(!!viewId && {
          context: new ManagedHttpContext({ publicAccessId: viewId }),
        }),
      })
      .pipe(
        tap((entry) => {
          const presentedEntry =
            this.dataEntryFactoryService.createPresenterModel(entry);
          const contextPresentedEntries =
            ctx.getState().contextRelationEntries[contextKey] ?? [];
          ctx.setState(
            patch<EntryStateModel>({
              contextRelationEntries: patch({
                [contextKey]: [...contextPresentedEntries, presentedEntry],
              }),
            }),
          );
        }),
        finalize(() => {
          ctx.dispatch(new CoreCountersActions.FetchCountersEntriesCount());
          ctx.dispatch(new LoadingActions.SetCommandLoading(false));

          ctx.setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                create: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntryActions.BatchCreateEntryWithRelatedEntries)
  batchCreateEntryWithRelatedEntries(
    ctx: StateContext<EntryStateModel>,
    {
      entityId,
      entry,
      viewId,
      selectEntry,
      relatedEntries,
      saveAsInvalid,
    }: EntryActions.BatchCreateEntryWithRelatedEntries,
  ): Observable<DataEntry> {
    const contextKey = EntryState.getContextKey(entityId, viewId);

    ctx.dispatch(new LoadingActions.SetCommandLoading(true));

    ctx.setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          create: true,
        }),
      }),
    );
    return this.entryDataService
      .batchCreate({
        entityId: entityId,
        body: {
          mainEntry: this.dataEntryFactoryService.createDataModel(entry),
          relatedEntries: relatedEntries,
          saveAsInvalid,
        },
        ...(!!viewId && {
          context: new ManagedHttpContext({ publicAccessId: viewId }),
        }),
      })
      .pipe(
        switchMap((res) =>
          this._fetchEntryAndUpdateState(
            ctx,
            entityId,
            contextKey,
            res.mainResult?.['id'],
          ),
        ),
        tap((res) => {
          ctx.dispatch(
            new DashboardActions.ClearPresentedStatisticByEntityId(entityId),
          );
          if (selectEntry) {
            ctx.dispatch(new EntryActions.SelectEntry(entityId, entry.id));
          }
        }),
        finalize(() => {
          ctx.dispatch(new CoreCountersActions.FetchCountersEntriesCount());
          ctx.dispatch(new LoadingActions.SetCommandLoading(false));
          if (entityId === SPACE_USERS_CORE_ENTITY_ID) {
            ctx.dispatch(new CoreSpaceActions.MySpaces());
          }

          ctx.setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                create: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntryActions.UpsertDraftEntry)
  upsertDraftEntry(
    { setState, dispatch }: StateContext<EntryStateModel>,
    { entityId, entry, viewId, draftId }: EntryActions.UpsertDraftEntry,
  ): Observable<DataEntry> {
    dispatch(new LoadingActions.SetCommandLoading(true));

    setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          create: true,
        }),
      }),
    );
    return this.entryDataService
      .upsertDraftEntry({
        entity: entityId,
        body: {
          ...this.dataEntryFactoryService.createDataModel(entry),
          draftEntryId: draftId ?? undefined,
        },
        ...(!!viewId && {
          context: new ManagedHttpContext({ publicAccessId: viewId }),
        }),
      })
      .pipe(
        tap((res) => {
          const presentedEntry =
            this.dataEntryFactoryService.createPresenterModel(res);

          //TODO: don't handle the presentedEntity here. instead, just dispatch the FetchEntry action
          const presentedEntryKey = this.getKey(entityId, presentedEntry.id);
          setState(
            patch<EntryStateModel>({
              presentedEntry: patch<PresentedEntry>({
                [presentedEntryKey]: {
                  entry: presentedEntry,
                  entity: this.store.selectSnapshot(
                    EntryState.presentedEntityForEntry(entityId, null),
                  ),
                },
              }),
              lastCreatedEntry: presentedEntryKey,
            }),
          );
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                create: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntryActions.UpdateEntry)
  updateEntry(
    ctx: StateContext<EntryStateModel>,
    {
      entityId,
      viewId,
      payload,
      shouldFetchEntry,
      refValues,
      systemButtonId,
      saveAsInvalid,
    }: EntryActions.UpdateEntry,
  ): Observable<any> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(entityId, viewId);
    ctx.setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          update: true,
        }),
      }),
    );
    const model = this.dataEntryFactoryService.createDataModel(payload);
    return this.entryDataService
      .updateById({
        entity: entityId,
        id: payload?.id,
        body: {
          ...model,
          referencedValues: refValues,
          systemButtonId,
          saveAsInvalid,
        },
        context: new ManagedHttpContext({ publicAccessId: viewId }),
      })
      .pipe(
        tap(() => {
          ctx.dispatch(
            new DashboardActions.ClearPresentedStatisticByEntityId(entityId),
          );
        }),
        switchMap(() => {
          if (shouldFetchEntry !== false) {
            return this._fetchEntryAndUpdateState(
              ctx,
              entityId,
              contextKey,
              payload.id,
            );
          }
          return of(null);
        }),
        finalize(() => {
          ctx.dispatch(new CoreCountersActions.FetchCountersEntriesCount());
          ctx.dispatch(new LoadingActions.SetCommandLoading(false));
          if (entityId === SPACE_USERS_CORE_ENTITY_ID) {
            ctx.dispatch(new CoreSpaceActions.MySpaces());
          }
          ctx.setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                update: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntryActions.UpdateEntryPosition)
  updateEntryPosition(
    ctx: StateContext<EntryStateModel>,
    { entityId, viewId, payload }: EntryActions.UpdateEntryPosition,
  ): Observable<any> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(entityId, viewId);
    ctx.setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          patch: true,
        }),
      }),
    );
    return this.entryDataService
      .updateEntryPosition({
        entity: entityId,
        id: payload?.id,
        body: {
          categoryField: payload.categoryField,
          column: payload.column,
          direction: payload.direction,
          changePriority: payload.changePriority,
          nextId: payload.nextId,
          previousId: payload.previousId,
        },
        context: new ManagedHttpContext({ publicAccessId: viewId }),
      })
      .pipe(
        tap((entry: DataEntry) => {
          const presentedEntry =
            this.dataEntryFactoryService.createPresenterModel(entry);
          if (payload.isKanban) {
            const existingKanbanEntries =
              ctx.getState().contextPresentedKanbanEntries[contextKey];
            const updatedKanbanEntries = structuredClone(
              existingKanbanEntries ?? {},
            );
            Object.keys(updatedKanbanEntries).forEach((key) => {
              if (
                key === payload.column ||
                (key === 'CATEGORY_NOT_SET' && payload.column === '')
              ) {
                const isExist = updatedKanbanEntries[key].items.some(
                  (x) => x.id === presentedEntry.id,
                );
                if (isExist) {
                  updatedKanbanEntries[key].items = updatedKanbanEntries[
                    key
                  ].items.map((i) =>
                    i.id !== presentedEntry.id ? i : presentedEntry,
                  );
                } else {
                  updatedKanbanEntries[key].items.push(presentedEntry);
                  updatedKanbanEntries[key].totalCount += 1;
                }
              } else {
                const isExist = updatedKanbanEntries[key].items.some(
                  (x) => x.id === presentedEntry.id,
                );
                if (isExist) {
                  updatedKanbanEntries[key].items = updatedKanbanEntries[
                    key
                  ].items.filter((i) => i.id !== presentedEntry.id);
                  updatedKanbanEntries[key].totalCount -= 1;
                }
              }
            });
            ctx.setState(
              patch<EntryStateModel>({
                contextPresentedKanbanEntries: patch({
                  [contextKey]: updatedKanbanEntries,
                }),
              }),
            );
          } else {
            ctx.setState(
              patch<EntryStateModel>({
                contextPresentedEntries: patch({
                  [contextKey]: updateItem(
                    (i) => i.id === payload.id,
                    presentedEntry,
                  ),
                }),
              }),
            );
          }
        }),
        finalize(() => {
          ctx.dispatch(new LoadingActions.SetCommandLoading(false));

          ctx.setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                patch: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntryActions.UpdateEntryRelationPosition)
  updateEntryRelationPosition(
    ctx: StateContext<EntryStateModel>,
    {
      entityId,
      viewId,
      relationId,
      payload,
    }: EntryActions.UpdateEntryRelationPosition,
  ): Observable<any> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(relationId, viewId);

    ctx.setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          patch: true,
        }),
      }),
    );
    return this.entryDataService
      .updateEntryPosition({
        entity: entityId,
        id: payload?.id,
        body: {
          categoryField: payload.categoryField,
          column: payload.column,
          direction: payload.direction,
          changePriority: payload.changePriority,
          nextId: payload.nextId,
          previousId: payload.previousId,
        },
        context: new ManagedHttpContext({ publicAccessId: viewId }),
      })
      .pipe(
        tap((entry: DataEntry) => {
          const presentedEntry =
            this.dataEntryFactoryService.createPresenterModel(entry);
          if (payload.isKanban) {
            const existingKanbanEntries =
              ctx.getState().contextPresentedRelationKanbanEntries[contextKey];
            const updatedKanbanEntries = structuredClone(
              existingKanbanEntries ?? {},
            );
            Object.keys(updatedKanbanEntries).forEach((key) => {
              if (
                key === payload.column ||
                (key === 'CATEGORY_NOT_SET' && payload.column === '')
              ) {
                const isExist = updatedKanbanEntries[key].items.some(
                  (x) => x.id === presentedEntry.id,
                );
                if (isExist) {
                  updatedKanbanEntries[key].items = updatedKanbanEntries[
                    key
                  ].items.map((i) =>
                    i.id !== presentedEntry.id ? i : presentedEntry,
                  );
                } else {
                  updatedKanbanEntries[key].items.push(presentedEntry);
                  updatedKanbanEntries[key].totalCount += 1;
                }
              } else {
                const isExist = updatedKanbanEntries[key].items.some(
                  (x) => x.id === presentedEntry.id,
                );
                if (isExist) {
                  updatedKanbanEntries[key].items = updatedKanbanEntries[
                    key
                  ].items.filter((i) => i.id !== presentedEntry.id);
                  updatedKanbanEntries[key].totalCount -= 1;
                }
              }
            });
            ctx.setState(
              patch<EntryStateModel>({
                contextPresentedRelationKanbanEntries: patch({
                  [contextKey]: updatedKanbanEntries,
                }),
              }),
            );
          } else {
            ctx.setState(
              patch<EntryStateModel>({
                contextRelationEntries: patch({
                  [contextKey]: updateItem(
                    (i) => i.id === payload.id,
                    presentedEntry,
                  ),
                }),
              }),
            );
          }
        }),
        finalize(() => {
          ctx.dispatch(new LoadingActions.SetCommandLoading(false));

          ctx.setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                patch: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntryActions.UpdateEntryCollectionPosition)
  updateEntryCollectionPosition(
    ctx: StateContext<EntryStateModel>,
    {
      entityId,
      viewId,
      collectionId,
      payload,
    }: EntryActions.UpdateEntryCollectionPosition,
  ): Observable<any> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(entityId, collectionId);
    ctx.setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          patch: true,
        }),
      }),
    );
    return this.entryDataService
      .updateEntryPosition({
        entity: entityId,
        id: payload?.id,
        body: {
          categoryField: payload.categoryField,
          column: payload.column,
          direction: payload.direction,
          changePriority: payload.changePriority,
          nextId: payload.nextId,
          previousId: payload.previousId,
        },
        context: new ManagedHttpContext({ publicAccessId: viewId }),
      })
      .pipe(
        tap((entry: DataEntry) => {
          const presentedEntry =
            this.dataEntryFactoryService.createPresenterModel(entry);
          ctx.setState(
            patch<EntryStateModel>({
              contextUserCollectionEntries: patch({
                [contextKey]: updateItem(
                  (i) => i.id === payload.id,
                  presentedEntry,
                ),
              }),
            }),
          );
        }),
        finalize(() => {
          ctx.dispatch(new LoadingActions.SetCommandLoading(false));

          ctx.setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                patch: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntryActions.UpdateEntryFieldValue)
  updateEntryFieldValue(
    ctx: StateContext<EntryStateModel>,
    { entityId, viewId, payload, entryId }: EntryActions.UpdateEntryFieldValue,
  ): Observable<any> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(entityId, viewId);
    ctx.setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          patch: true,
        }),
      }),
    );

    return this.entryDataService
      .patchDataEntryV3({
        entity: entityId,
        id: entryId,
        body: [this.getPatchedEntryFieldValueV3(payload)],
        context: new ManagedHttpContext({ publicAccessId: viewId }),
      })
      .pipe(
        tap(() => {
          ctx.setState(
            patch<EntryStateModel>({
              patchEntryStatus: patch({
                [EntryState.getEntryPatchKey(entryId, payload.fieldUId)]: {
                  status: 'success',
                },
              }),
            }),
          );
        }),
        switchMap(() =>
          this._fetchEntryAndUpdateState(ctx, entityId, contextKey, entryId),
        ),
        catchError((err: unknown) => {
          ctx.setState(
            patch<EntryStateModel>({
              patchEntryStatus: patch({
                [EntryState.getEntryPatchKey(entryId, payload.fieldUId)]: {
                  status: 'error',
                  error: 'There was error when patching',
                },
              }),
            }),
          );
          return throwError(() => err);
        }),
        finalize(() => {
          ctx.dispatch(new LoadingActions.SetCommandLoading(false));

          ctx.setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                patch: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntryActions.UpdateEntryReferenceFieldMappedValues)
  updateEntryReferenceFieldMappedValues(
    ctx: StateContext<EntryStateModel>,
    {
      entityId,
      viewId,
      payload,
      entryId,
    }: EntryActions.UpdateEntryReferenceFieldMappedValues,
  ): Observable<any> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(entityId, viewId);
    ctx.setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          patch: true,
        }),
      }),
    );

    return this.entryDataService
      .patchDataEntryV3({
        entity: entityId,
        id: entryId,
        body: [
          {
            mode: 0,
            patchOperations: payload
              .map((pathObj) => this.getPatchedEntryFieldValueV3(pathObj))
              ?.map((x) => x.patchOperations[0]),
          },
        ],
        context: new ManagedHttpContext({ publicAccessId: viewId }),
      })
      .pipe(
        tap(() => {
          ctx.setState(
            patch<EntryStateModel>({
              patchEntryStatus: patch({
                [EntryState.getEntryPatchKey(entryId, payload?.[0]?.fieldUId)]:
                  {
                    status: 'success',
                  },
              }),
            }),
          );
        }),
        switchMap(() =>
          this._fetchEntryAndUpdateState(ctx, entityId, contextKey, entryId),
        ),
        catchError((err: unknown) => {
          ctx.setState(
            patch<EntryStateModel>({
              patchEntryStatus: patch({
                [EntryState.getEntryPatchKey(entryId, payload?.[0]?.fieldUId)]:
                  {
                    status: 'error',
                    error: 'There was error when patching',
                  },
              }),
            }),
          );
          return throwError(() => err);
        }),
        finalize(() => {
          ctx.dispatch(new LoadingActions.SetCommandLoading(false));

          ctx.setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                patch: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntryActions.UpdateRelationEntryFieldValue)
  updateRelationEntryFieldValue(
    ctx: StateContext<EntryStateModel>,
    {
      entityId,
      viewId,
      payload,
      entryId,
      relationId,
    }: EntryActions.UpdateRelationEntryFieldValue,
  ): Observable<any> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(relationId, viewId);

    ctx.setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          patch: true,
        }),
      }),
    );

    return this.entryDataService
      .updateByIdV2({
        entity: entityId,
        id: entryId,
        body: [this.getPatchedEntryFieldValue(payload)],
        context: new ManagedHttpContext({ publicAccessId: viewId }),
      })
      .pipe(
        tap((entry: DataEntry) => {
          const presentedEntry =
            this.dataEntryFactoryService.createPresenterModel(entry);
          const existingKanbanEntries =
            ctx.getState().contextPresentedRelationKanbanEntries[contextKey];
          const updatedKanbanEntries = structuredClone(
            existingKanbanEntries ?? {},
          );
          Object.keys(updatedKanbanEntries).forEach((key) => {
            if (
              !!updatedKanbanEntries[key] &&
              updatedKanbanEntries[key].items?.length
            ) {
              updatedKanbanEntries[key].items = updatedKanbanEntries[
                key
              ].items.map((item) => {
                if (item.id === entryId) {
                  return entry;
                }
                return item;
              });
            }
          });
          ctx.setState(
            patch<EntryStateModel>({
              contextPresentedRelationKanbanEntries: patch({
                [contextKey]: updatedKanbanEntries,
              }),
              contextRelationEntries: patch({
                [contextKey]: updateItem(
                  (i) => i.id === entryId,
                  presentedEntry,
                ),
              }),
              patchEntryStatus: patch({
                [EntryState.getEntryPatchKey(entryId, payload.fieldUId)]: {
                  status: 'success',
                },
              }),
            }),
          );
        }),
        catchError((err: unknown) => {
          ctx.setState(
            patch<EntryStateModel>({
              patchEntryStatus: patch({
                [EntryState.getEntryPatchKey(entryId, payload.fieldUId)]: {
                  status: 'error',
                  error: 'There was error when patching',
                },
              }),
            }),
          );
          return throwError(() => err);
        }),
        finalize(() => {
          ctx.dispatch(new LoadingActions.SetCommandLoading(false));

          ctx.setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                patch: false,
              }),
            }),
          );
        }),
      );
  }
  @Action(EntryActions.UpdateRelationEntryReferenceFieldMappedValues)
  UpdateRelationEntryReferenceFieldMappedValues(
    ctx: StateContext<EntryStateModel>,
    {
      entityId,
      viewId,
      payload,
      entryId,
      relationId,
    }: EntryActions.UpdateRelationEntryReferenceFieldMappedValues,
  ): Observable<any> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(relationId, viewId);

    ctx.setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          patch: true,
        }),
      }),
    );

    return this.entryDataService
      .updateByIdV2({
        entity: entityId,
        id: entryId,
        body: [
          ...payload.map((pathObj) => this.getPatchedEntryFieldValue(pathObj)),
        ],
        context: new ManagedHttpContext({ publicAccessId: viewId }),
      })
      .pipe(
        tap((entry: DataEntry) => {
          const presentedEntry =
            this.dataEntryFactoryService.createPresenterModel(entry);
          const existingKanbanEntries =
            ctx.getState().contextPresentedRelationKanbanEntries[contextKey];
          const updatedKanbanEntries = structuredClone(
            existingKanbanEntries ?? {},
          );
          Object.keys(updatedKanbanEntries).forEach((key) => {
            if (
              !!updatedKanbanEntries[key] &&
              updatedKanbanEntries[key].items?.length
            ) {
              updatedKanbanEntries[key].items = updatedKanbanEntries[
                key
              ].items.map((item) => {
                if (item.id === entryId) {
                  return entry;
                }
                return item;
              });
            }
          });
          ctx.setState(
            patch<EntryStateModel>({
              contextPresentedRelationKanbanEntries: patch({
                [contextKey]: updatedKanbanEntries,
              }),
              contextRelationEntries: patch({
                [contextKey]: updateItem(
                  (i) => i.id === entryId,
                  presentedEntry,
                ),
              }),
              patchEntryStatus: patch({
                [EntryState.getEntryPatchKey(entryId, payload?.[0]?.fieldUId)]:
                  {
                    status: 'success',
                  },
              }),
            }),
          );
        }),
        catchError((err: unknown) => {
          ctx.setState(
            patch<EntryStateModel>({
              patchEntryStatus: patch({
                [EntryState.getEntryPatchKey(entryId, payload?.[0]?.fieldUId)]:
                  {
                    status: 'error',
                    error: 'There was error when patching',
                  },
              }),
            }),
          );
          return throwError(() => err);
        }),
        finalize(() => {
          ctx.dispatch(new LoadingActions.SetCommandLoading(false));

          ctx.setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                patch: false,
              }),
            }),
          );
        }),
      );
  }
  @Action(EntryActions.UpdateCollectionEntryFieldValue)
  updateCollectionEntryFieldValue(
    ctx: StateContext<EntryStateModel>,
    {
      entityId,
      viewId,
      payload,
      entryId,
      collectionId,
    }: EntryActions.UpdateCollectionEntryFieldValue,
  ): Observable<any> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(entityId, collectionId);

    ctx.setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          patch: true,
        }),
      }),
    );

    return this.entryDataService
      .updateByIdV2({
        entity: entityId,
        id: entryId,
        body: [this.getPatchedEntryFieldValue(payload)],
        context: new ManagedHttpContext({ publicAccessId: viewId }),
      })
      .pipe(
        tap((entry: DataEntry) => {
          const presentedEntry =
            this.dataEntryFactoryService.createPresenterModel(entry);
          ctx.setState(
            patch<EntryStateModel>({
              contextUserCollectionEntries: patch({
                [contextKey]: updateItem(
                  (i) => i.id === entryId,
                  presentedEntry,
                ),
              }),
              patchEntryStatus: patch({
                [EntryState.getEntryPatchKey(entryId, payload.fieldUId)]: {
                  status: 'success',
                },
              }),
            }),
          );
        }),
        catchError((err: unknown) => {
          ctx.setState(
            patch<EntryStateModel>({
              patchEntryStatus: patch({
                [EntryState.getEntryPatchKey(entryId, payload.fieldUId)]: {
                  status: 'error',
                  error: 'There was error when patching',
                },
              }),
            }),
          );
          return throwError(() => err);
        }),
        finalize(() => {
          ctx.dispatch(new LoadingActions.SetCommandLoading(false));

          ctx.setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                patch: false,
              }),
            }),
          );
        }),
      );
  }
  @Action(EntryActions.UpdateCollectionEntryReferenceFieldMappedValues)
  updateCollectionEntryReferenceFieldMappedValue(
    ctx: StateContext<EntryStateModel>,
    {
      entityId,
      viewId,
      payload,
      entryId,
      collectionId,
    }: EntryActions.UpdateCollectionEntryReferenceFieldMappedValues,
  ): Observable<any> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(entityId, collectionId);

    ctx.setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          patch: true,
        }),
      }),
    );

    return this.entryDataService
      .updateByIdV2({
        entity: entityId,
        id: entryId,
        body: [
          ...payload.map((pathObj) => this.getPatchedEntryFieldValue(pathObj)),
        ],
        context: new ManagedHttpContext({ publicAccessId: viewId }),
      })
      .pipe(
        tap((entry: DataEntry) => {
          const presentedEntry =
            this.dataEntryFactoryService.createPresenterModel(entry);
          ctx.setState(
            patch<EntryStateModel>({
              contextUserCollectionEntries: patch({
                [contextKey]: updateItem(
                  (i) => i.id === entryId,
                  presentedEntry,
                ),
              }),
              patchEntryStatus: patch({
                [EntryState.getEntryPatchKey(entryId, payload?.[0]?.fieldUId)]:
                  {
                    status: 'success',
                  },
              }),
            }),
          );
        }),
        catchError((err: unknown) => {
          ctx.setState(
            patch<EntryStateModel>({
              patchEntryStatus: patch({
                [EntryState.getEntryPatchKey(entryId, payload?.[0]?.fieldUId)]:
                  {
                    status: 'error',
                    error: 'There was error when patching',
                  },
              }),
            }),
          );
          return throwError(() => err);
        }),
        finalize(() => {
          ctx.dispatch(new LoadingActions.SetCommandLoading(false));

          ctx.setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                patch: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntryActions.UpgradeEntry)
  upgradeEntry(
    ctx: StateContext<EntryStateModel>,
    { entityId, viewId, payload }: EntryActions.UpgradeEntry,
  ): Observable<any> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(entityId, viewId);

    ctx.setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          update: true,
        }),
      }),
    );
    const model = this.dataEntryFactoryService.createDataModel(payload);
    return this.entryDataService
      .upgradeEntry({
        entity: entityId,
        id: payload?.id,
        body: model,
      })
      .pipe(
        tap(() => {
          ctx.dispatch(
            new DashboardActions.ClearPresentedStatisticByEntityId(entityId),
          );
        }),
        switchMap(() =>
          this._fetchEntryAndUpdateState(
            ctx,
            entityId,
            contextKey,
            payload?.id,
          ),
        ),
        finalize(() => {
          ctx.dispatch(new CoreCountersActions.FetchCountersEntriesCount());
          ctx.dispatch(new LoadingActions.SetCommandLoading(false));

          ctx.setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                update: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntryActions.FetchEntryReferenceMappedFieldsValues)
  fetchEntryReferenceMappedFieldsValues(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.FetchEntryReferenceMappedFieldsValues,
  ): Observable<any> {
    return this.entryDataService
      .getMappedFieldsData({
        entity: payload.entity,
        entryId: payload.entryId,
        referenceDataEntry: payload?.referenceEntryId,
        referenceFieldId: payload?.referenceFieldId,
      })
      .pipe(
        tap((result) => {
          ctx.setState(
            patch<EntryStateModel>({
              mappedFieldsValues: patch<{
                [key: string]: MappingFieldSourcesModel[];
              }>({
                [this.getKey(
                  payload.referenceFieldId,
                  payload.referenceEntryId,
                )]: result,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntryActions.ArchiveEntry)
  archiveEntry(
    { setState, dispatch }: StateContext<EntryStateModel>,
    { entityId, viewId, payload }: EntryActions.ArchiveEntry,
  ) {
    dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(entityId, viewId);

    setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          update: true,
          remove: true,
        }),
      }),
    );
    return this.entryDataService
      .archiveEntry({
        entity: entityId,
        id: payload?.id,
      })
      .pipe(
        tap(() => {
          dispatch(
            new DashboardActions.ClearPresentedStatisticByEntityId(entityId),
          );
        }),
        switchMap(() =>
          this.entryDataService.findById({ entity: entityId, id: payload.id }),
        ),
        tap((res) => {
          const presenter =
            this.dataEntryFactoryService.createPresenterModel(res);
          setState(
            patch<EntryStateModel>({
              contextPresentedEntries: patch({
                [contextKey]: updateItem((i) => i.id === payload.id, presenter),
              }),

              presentedEntry: patch<PresentedEntry>({
                [this.getKey(entityId, res.id)]: patch({
                  entry: presenter,
                }),
              }),
            }),
          );
        }),
        finalize(() => {
          dispatch(new CoreCountersActions.FetchCountersEntriesCount());
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                update: false,
                remove: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntryActions.RemoveDraftEntry)
  deleteDraftEntry(
    { setState, dispatch }: StateContext<EntryStateModel>,
    { entityId, payload }: EntryActions.RemoveDraftEntry,
  ) {
    dispatch(new LoadingActions.SetCommandLoading(true));

    setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          remove: true,
        }),
      }),
    );
    return this.entryDataService
      .deleteDraftEntry({
        entity: entityId,
        id: payload?.id,
      })
      .pipe(
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                remove: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntryActions.ArchiveEntries)
  archiveEntries(
    { setState, dispatch, getState }: StateContext<EntryStateModel>,
    {
      entityId,
      viewId,
      entryIds = [],
      selectAll,
      relationId,
    }: EntryActions.ArchiveEntries,
  ): Observable<void> {
    dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(relationId ?? entityId, viewId);
    setState(
      patch<EntryStateModel>({
        loading: patch<LoadingIndicator>({
          update: true,
          remove: true,
        }),
      }),
    );

    const selectAllEnabled = selectAll?.enabled;
    const body = {
      dataEntriesIds: selectAllEnabled ? [] : entryIds,
      selectAll: selectAll ?? null,
    } satisfies ArchiveDataEntryDtoModel;

    return this.entryDataService
      .archiveEntriesV2({
        entity: entityId,
        body,
      })
      .pipe(
        tap((_) => {
          const composedOperators = entryIds.map((id) =>
            removeItem<DataEntryPresenterModel>((entry) => entry.id === id),
          );
          const presentedEntriesKeys = Object.keys(getState().presentedEntry);
          const presentedEntriesOperators = entryIds
            .filter(
              (id) =>
                !!presentedEntriesKeys.includes(this.getKey(entityId, id)),
            )
            .map((id) =>
              patch<PresentedEntry>({
                [this.getKey(entityId, id)]: patch({
                  entry: patch<DataEntryPresenterModel>({
                    state: DataEntryStateEnum.Archived,
                  }),
                }),
              }),
            );

          setState(
            patch<EntryStateModel>(
              !relationId
                ? {
                    // @TODO replace with updateManyItems _WAIT
                    contextPresentedEntries: patch({
                      [contextKey]: compose(...composedOperators),
                    }),
                    presentedEntry: compose(...presentedEntriesOperators),
                  }
                : {
                    // @TODO replace with updateManyItems _WAIT
                    contextRelationEntries: patch({
                      [contextKey]: compose(...composedOperators),
                    }),
                  },
            ),
          );
        }),
        finalize(() => {
          dispatch(new CoreCountersActions.FetchCountersEntriesCount());
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<EntryStateModel>({
              loading: patch<LoadingIndicator>({
                update: false,
                remove: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntityActions.ExecuteWorkflowAction)
  executeWorkflowAction(
    ctx: StateContext<EntryStateModel>,
    action: EntityActions.ExecuteWorkflowAction,
  ): Observable<DataEntry> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(action.entity, action.viewId);

    return this.workflowService
      .executeWorkflowAction({
        entity: action.entity,
        entry: action.entry,
        workflowId: action.workflowId,
        body: {
          action: action.actionId,
          notes: action.notes,
        },
        context: new ManagedHttpContext({ bypassErrorMiddleware: true }),
      })
      .pipe(
        switchMap(() =>
          this._fetchEntryAndUpdateState(
            ctx,
            action.entity,
            contextKey,
            action.entry,
          ),
        ),
        tap(() => {
          ctx.setState(
            patch<EntryStateModel>({
              executeWorkFlowErrors: null,
            }),
          );
          if (action.entityType === DataEntityTypeEnum.Request) {
            ctx.dispatch(
              new NavigationStateActions.FetchItemsPinnedToSidebarV2(),
            );
          }
        }),
        catchError((err: unknown) => {
          const error: ApiErrorModel = (err as HttpErrorResponse)?.error
            ?.result as ApiErrorModel;

          const validationError =
            WORKFLOW_ERROR_CODES.has(error.errorCode) &&
            (error.requestCode === 400 || error.requestCode === 422);

          ctx.setState(
            patch<EntryStateModel>({
              executeWorkFlowErrors: {
                validationError,
              },
            }),
          );
          return EMPTY;
        }),
        finalize(() =>
          ctx.dispatch(new LoadingActions.SetCommandLoading(false)),
        ),
      );
  }

  @Action(EntityActions.BulkExecuteWorkflowAction)
  BulkExecuteWorkflowAction(
    ctx: StateContext<EntryStateModel>,
    action: EntityActions.BulkExecuteWorkflowAction,
  ) {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));

    return this.workflowService
      .bulkExecuteWorkflowAction({
        entity: action.entity,
        workflowId: action.workflowId,
        actionId: action.actionId,
        body: {
          entryIds: action.entryIds,
          notes: action.notes,
        },
      })
      .pipe(
        tap((result) => {
          ctx.setState(
            patch<EntryStateModel>({
              bulkExecuteWorkflowResult: result as any,
            }),
          );
        }),
        finalize(() =>
          ctx.dispatch(new LoadingActions.SetCommandLoading(false)),
        ),
      );
  }

  @Action(EntityActions.ReturnToPreviousStep)
  changeCurrentStep(
    ctx: StateContext<EntryStateModel>,
    {
      entity,
      workflowId,
      entry,
      step,
      note,
      viewId,
    }: EntityActions.ReturnToPreviousStep,
  ): Observable<any> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(entity, viewId);

    return this.workflowService
      .changeCurrentStep({
        entity,
        entry,
        workflowId,
        body: {
          stepId: step,
          notes: note,
          changeStepMode: ChangeStepModeEnum.ReturnToStep,
        },
      })
      .pipe(
        switchMap(() =>
          this._fetchEntryAndUpdateState(ctx, entity, contextKey, entry),
        ),
        finalize(() =>
          ctx.dispatch(new LoadingActions.SetCommandLoading(false)),
        ),
      );
  }

  @Action(EntityActions.CancelWorkflow)
  cancelWorkflow(
    ctx: StateContext<EntryStateModel>,
    { entity, workflowId, entry, note, viewId },
  ): Observable<DataEntry> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(entity, viewId);

    return this.workflowService
      .changeCurrentStep({
        entity,
        entry,
        workflowId,
        body: {
          stepId: '',
          notes: note,
          changeStepMode: ChangeStepModeEnum.EndWorkflow,
        },
      })
      .pipe(
        switchMap(() =>
          this._fetchEntryAndUpdateState(ctx, entity, contextKey, entry),
        ),
        finalize(() =>
          ctx.dispatch(new LoadingActions.SetCommandLoading(false)),
        ),
      );
  }

  @Action(EntityActions.ResetWorkflow)
  resetWorkflow(
    ctx: StateContext<EntryStateModel>,
    { entity, workflowId, entry, note, viewId }: EntityActions.ResetWorkflow,
  ): Observable<DataEntry> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(entity, viewId);
    return this.workflowService
      .changeCurrentStep({
        entity,
        entry,
        workflowId,
        body: {
          notes: note ?? undefined,
          changeStepMode: ChangeStepModeEnum.ResetWorkflow,
          stepId: '',
        },
      })
      .pipe(
        switchMap(() =>
          this._fetchEntryAndUpdateState(ctx, entity, contextKey, entry),
        ),
        finalize(() =>
          ctx.dispatch(new LoadingActions.SetCommandLoading(false)),
        ),
      );
  }

  @Action(EntityActions.MoveToStep)
  moveToStep(
    ctx: StateContext<EntryStateModel>,
    { entity, workflowId, entry, step, note, viewId }: EntityActions.MoveToStep,
  ): Observable<DataEntry> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(entity, viewId);
    // Change later
    return this.workflowService
      .changeCurrentStep({
        entity,
        entry,
        workflowId,
        body: {
          stepId: step,
          notes: note,
          changeStepMode: ChangeStepModeEnum.MoveToStep,
        },
      })
      .pipe(
        switchMap(() =>
          this._fetchEntryAndUpdateState(ctx, entity, contextKey, entry),
        ),
        finalize(() =>
          ctx.dispatch(new LoadingActions.SetCommandLoading(false)),
        ),
      );
  }

  @Action(EntityActions.CreateAdhocStep)
  createAdhocStep(
    ctx: StateContext<EntryStateModel>,
    { entity, workflowId, entry, body, viewId }: EntityActions.CreateAdhocStep,
  ): Observable<DataEntry> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(entity.id, viewId);
    return this.workflowService
      .addAdHocStep({
        entity: entity.id,
        entry,
        workflowId,
        body,
      })
      .pipe(
        switchMap(() =>
          this._fetchEntryAndUpdateState(ctx, entity.id, contextKey, entry),
        ),
        finalize(() =>
          ctx.dispatch(new LoadingActions.SetCommandLoading(false)),
        ),
      );
  }

  @Action(EntryActions.ChangeCurrentStepOwner)
  changeCurrentStepOwner(
    ctx: StateContext<EntryStateModel>,
    {
      entity,
      workflowId,
      entry,
      body,
      viewId,
    }: EntryActions.ChangeCurrentStepOwner,
  ): Observable<DataEntry> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const contextKey = EntryState.getContextKey(entity, viewId);
    return this.workflowService
      .changeCurrentStepOwnerV2({
        entity,
        entry,
        workflowId,
        body,
      })
      .pipe(
        switchMap(() =>
          this._fetchEntryAndUpdateState(ctx, entity, contextKey, entry),
        ),
        finalize(() =>
          ctx.dispatch(new LoadingActions.SetCommandLoading(false)),
        ),
      );
  }

  @Action(EntryActions.ClearAppliedFilter)
  clearAppliedFilter({ setState }: StateContext<EntryStateModel>) {
    setState(
      patch<EntryStateModel>({
        contextAppliedFilter: {},
      }),
    );
  }

  @Action(EntryActions.ClearEntityPresentedEntries)
  clearEntityPresentedEntries(
    ctx: StateContext<EntryStateModel>,
    { entityId }: EntryActions.ClearEntityPresentedEntries,
  ) {
    this._clearEntityEntries(ctx, entityId);
  }

  @Action(EntryActions.ClearPresentedEntries)
  clearPresentedEntries({ setState }: StateContext<EntryStateModel>) {
    setState(
      patch<EntryStateModel>({
        presentedEntry: {},
      }),
    );
  }

  @Action(EntryActions.SetPendingDataQueryForView)
  setPendingDataQueryForView(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.SetPendingDataQueryForView,
  ) {
    if (payload.dataQuery) {
      ctx.setState(
        patch<EntryStateModel>({
          pendingDataQueryByView: patch({
            [payload.dataViewId]: payload.dataQuery,
          }),
        }),
      );
    } else if (payload.dataQueryId) {
      return this.dataQueryService
        .findMyQueries({
          filter: this.apiHelper.prepareFilter({
            where: { id: payload.dataQueryId },
          }),
          // Since this action (SetPendingDataQueryForView) could be dispatched from the router's resolver, the public AccessId might not be ready yet,
          // so the view's Id should be used as an AccessId.
          context: new ManagedHttpContext({
            publicAccessId: payload.dataViewId,
          }),
        })
        .pipe(
          map((res) => this.queryFactoryService.createPresenterModel(res[0])),
          tap((query) => {
            ctx.setState(
              patch<EntryStateModel>({
                pendingDataQueryByView: patch({
                  [payload.dataViewId]: query,
                }),
              }),
            );
          }),
        );
    }

    return EMPTY;
  }

  @Action(EntryActions.FetchEntryHistory)
  fetchEntryHistory(
    ctx: StateContext<EntryStateModel>,
    {
      payload: { entryId, entityId, lastTimeStamp },
    }: EntryActions.FetchEntryHistory,
  ): Observable<any> {
    const oldHistory = ctx.getState().selectedEntryHistory ?? [];
    return this.entryDataService
      .findEntryHistoryByIdV2({ entity: entityId, id: entryId, lastTimeStamp })
      .pipe(
        map((res) =>
          res.map((entryHistory) =>
            this.dataEntryHistoryFactoryService.createPresenterModel(
              entryHistory,
            ),
          ),
        ),
        tap((histories) => {
          if (!lastTimeStamp && oldHistory?.length) {
            // get records that doesn't exists inside oldHistory
            const newHistories = histories.filter(
              (history) =>
                !oldHistory.some((oldHistory) => isEqual(oldHistory, history)),
            );
            ctx.setState(
              patch<EntryStateModel>({
                selectedEntryHistory: oldHistory?.concat(newHistories),
              }),
            );
          } else {
            ctx.setState(
              patch<EntryStateModel>({
                selectedEntryHistory: oldHistory?.concat(histories),
              }),
            );
          }
        }),
      );
  }

  @Action(EntryActions.FetchOwnerGroupUsers)
  fetchOwnerGroupUsers(
    ctx: StateContext<EntryStateModel>,
    { payload: { groupId } }: EntryActions.FetchOwnerGroupUsers,
  ): Observable<any> {
    return this.groupsService
      .getUsersInsideGroup({ groupId, includeResolved: false })
      .pipe(
        tap((usersRefProperties) => {
          ctx.setState(
            patch<EntryStateModel>({
              groupOwnerUsers: usersRefProperties,
            }),
          );
        }),
      );
  }

  @Action(EntryActions.BulkUpgradeEntries)
  bulkUpgradeEntries(
    ctx: StateContext<EntryStateModel>,
    { entity, payload }: EntryActions.BulkUpgradeEntries,
  ): Observable<any> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const selectAllEnabled = payload.selectAllModel?.enabled;

    const bodyV2 = {
      resetWorkflow: payload.resetWorkflow,
      dataEntriesIds: selectAllEnabled ? undefined : payload.dataEntriesIds,
      selectAllModel: payload.selectAllModel ?? null,
      requiredTransformations: payload.requiredTransformations?.map(
        (versionTransform) => ({
          sourceVersion: versionTransform.sourceVersion,
          transformations: versionTransform.transformations?.map((x) =>
            this.transformationFactory.createDataModel(x),
          ),
        }),
      ),
      additionalTransformations: payload.additionalTransformations?.map(
        (versionTransform) => ({
          sourceVersion: versionTransform.sourceVersion,
          transformations: versionTransform.transformations?.map((x) =>
            this.transformationFactory.createDataModel(x),
          ),
        }),
      ),
    };
    return this.entryDataService
      .bulkUpgradeEntriesV2({
        entity,
        body: bodyV2,
      })
      .pipe(
        tap((result) => {
          ctx.setState(
            patch<EntryStateModel>({
              bulkUpgradeResult: result as any,
            }),
          );
        }),
        finalize(() =>
          ctx.dispatch(new LoadingActions.SetCommandLoading(false)),
        ),
      );
  }

  @Action(EntryActions.BulkUpdateEntries)
  bulkUpdateEntries(
    ctx: StateContext<EntryStateModel>,
    { entity, payload }: EntryActions.BulkUpdateEntries,
  ): Observable<any> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const selectAllEnabled = payload.selectAllModel?.enabled;
    const body = {
      saveInvalidEntries: payload.saveInvalidEntries,
      dataEntriesIds: selectAllEnabled ? [] : payload.dataEntriesIds,
      selectAllModel: payload.selectAllModel ?? null,
      updateDefinitions: payload.updateDefinitions,
    };
    return this.entryDataService
      .bulkUpdateEntries({
        entity,
        body: body,
      })
      .pipe(
        tap((result) => {
          ctx.setState(
            patch<EntryStateModel>({
              bulkUpdateResult: result as any,
            }),
          );
        }),
        finalize(() =>
          ctx.dispatch(new LoadingActions.SetCommandLoading(false)),
        ),
      );
  }

  private _fetchEntryAndUpdateState(
    ctx: StateContext<EntryStateModel>,
    entityId,
    contextKey: string,
    dataEntry: string | DataEntryPresenterModel,
  ): Observable<any> {
    const entryId = typeof dataEntry === 'string' ? dataEntry : dataEntry.id;
    const source: Observable<{
      entry: DataEntryPresenterModel;
      entity: DataEntityPresenterModel;
      warnings: {
        errorMessage?: LocalizedValueModel;
      };
    }> =
      typeof dataEntry === 'string'
        ? this.entryDataService
            .findEntityAndEntryById({
              entity: entityId,
              id: dataEntry,
            })
            .pipe(
              map(
                ({
                  entry,
                  entity,
                  warnings,
                  canExecuteWorkflowAction,
                  canTakeOwnership,
                }) => {
                  const { presentedEntity, presentedEntry } =
                    this._mapPresenterEntryAndEntity(entity, entry);
                  return {
                    entity: presentedEntity,
                    entry: presentedEntry,
                    warnings,
                    canExecuteWorkflowAction,
                    canTakeOwnership,
                  };
                },
              ),
            )
        : of({
            entry: this.dataEntryFactoryService.createPresenterModel(
              dataEntry as DataEntry,
            ),
            entity: this.getEntryEntityFromState(ctx, entityId, entryId),
            warnings: null,
          });
    return source.pipe(
      tap(
        ({
          entry,
          entity,
          warnings,
          canExecuteWorkflowAction,
          canTakeOwnership,
        }) => {
          const entryKey = this.getKey(entityId, entryId);
          const existingKanbanEntries =
            ctx.getState().contextPresentedKanbanEntries[contextKey];
          const updatedKanbanEntries = structuredClone(
            existingKanbanEntries ?? {},
          );
          if (updatedKanbanEntries) {
            Object.keys(updatedKanbanEntries).forEach((key) => {
              if (
                !!updatedKanbanEntries[key] &&
                updatedKanbanEntries[key].items?.length
              ) {
                updatedKanbanEntries[key].items = updatedKanbanEntries[
                  key
                ].items.map((item) => {
                  if (item.id === entryId) {
                    return entry;
                  }
                  return item;
                });
              }
            });
          }
          const contextPresentedEntries =
            ctx.getState().contextPresentedEntries[contextKey] ?? [];
          ctx.setState(
            patch<EntryStateModel>({
              contextPresentedEntries: patch({
                [contextKey]: contextPresentedEntries.some(
                  (x) => x.id === entryId,
                )
                  ? contextPresentedEntries?.map((item) => {
                      if (item.id === entryId) {
                        return entry;
                      }
                      return item;
                    })
                  : [...contextPresentedEntries, entry],
              }),
              contextPresentedKanbanEntries: patch({
                [contextKey]: updatedKanbanEntries,
              }),
              presentedEntry: patch<PresentedEntry>({
                [entryKey]: {
                  entity: entity,
                  entry: entry,
                  canExecuteWorkflowAction,
                  canTakeOwnership,
                },
              }),
              lastUpdatedEntry: entryKey,
              lastCreatedEntry: entryKey,

              presentedEntryWarnings: patch({
                [entryKey]: warnings?.length
                  ? warnings.map((warning) => ({
                      message: warning.errorMessage,
                    }))
                  : null,
              }),
            }),
          );
        },
      ),
    );
  }

  private _mapPresenterEntries(
    ctx: StateContext<EntryStateModel>,
    entityId: string,
    entries: DataEntry[],
    normalizeEntry = false,
  ): Observable<DataEntryPresenterModel[]> {
    const items: DataEntryPresenterModel[] = entries.map((x) =>
      this.dataEntryFactoryService.createPresenterModel(x),
    );
    if (normalizeEntry) {
      return this._getDataEntity(ctx, entityId).pipe(
        map((entity) =>
          items.map((item) => normalizePresentedEntry(item, entity)),
        ),
      );
    }
    return of(items);
  }

  private _mapPresenterEntriesForKanban(
    ctx: StateContext<EntryStateModel>,
    entityId: string,
    columns: DataEntryKanbanViewColumn[],
    normalizeEntry = false,
  ): Observable<
    {
      categoryId?: string;
      columnCount: number;
      items: DataEntryPresenterModel[];
    }[]
  > {
    const items = columns.map((x) => {
      return {
        categoryId: x.category?.id,
        columnCount: x.totalCount,
        items: x.entries?.map((z) =>
          this.dataEntryFactoryService.createPresenterModel(z),
        ),
      };
    });

    if (normalizeEntry) {
      return this._getDataEntity(ctx, entityId).pipe(
        map((entity) =>
          items.map((item) => ({
            categoryId: item.categoryId, // Corrected typo here
            columnCount: item.columnCount,
            items: item.items.map((x) => normalizePresentedEntry(x, entity)),
          })),
        ),
      );
    }

    return of(items);
  }
  private _mapPresenterEntryAndEntity(
    entity: DataEntity,
    entry: DataEntry,
    normalizeEntry = false,
  ): {
    presentedEntry: DataEntryPresenterModel;
    presentedEntity: DataEntityPresenterModel;
  } {
    let presentedEntry = entry
      ? this.dataEntryFactoryService.createPresenterModel(entry)
      : null;
    const presentedEntity =
      this.dataEntityFactoryService.createPresenterModel(entity);
    if (normalizeEntry && !!presentedEntry) {
      presentedEntry = normalizePresentedEntry(presentedEntry, presentedEntity);
    }
    return {
      presentedEntry: presentedEntry,
      presentedEntity: presentedEntity,
    };
  }

  private _getDataEntity(
    ctx: StateContext<EntryStateModel>,
    entityId: string,
  ): Observable<DataEntityPresenterModel> {
    return ctx
      .dispatch(new EntityActions.FetchEntity(entityId))
      .pipe(
        map(() =>
          this.store.selectSnapshot(EntityState.presentedEntity(entityId)),
        ),
      );
  }

  private _clearEntityEntries(
    ctx: StateContext<EntryStateModel>,
    entityId: string,
    excludedKeys: string[] = [],
  ): void {
    // Clear presentedEntry
    const presentedEntries = ctx.getState().presentedEntry;
    const entityEntriesKeys = Object.keys(presentedEntries).filter((key) => {
      return key.startsWith(entityId) && !excludedKeys.includes(key);
    });

    // TODO replace with UpdateManyItems after updating it to accept object
    ctx.setState(
      patch<EntryStateModel>({
        presentedEntry: compose(
          ...entityEntriesKeys.map((key) =>
            patch<PresentedEntry>({ [key]: null }),
          ),
        ),
      }),
    );
  }
  @Action(EntryActions.TransitoryFetchActionEntriesCount)
  getActiveEntriesCount(
    ctx: StateContext<EntryStateModel>,
    { entityId, viewId }: EntryActions.TransitoryFetchActionEntriesCount,
  ): Observable<any> {
    return this.entryDataService
      .count({
        entity: entityId,
        where: this.apiHelper.prepareWhere<DataEntry>({
          state: DataEntryStateEnum.Active,
        }),
        context: viewId
          ? new ManagedHttpContext({ publicAccessId: viewId })
          : undefined,
      })
      .pipe(
        map((count) => count.count),
        tap((count) => {
          ctx.setState(
            patch<EntryStateModel>({
              transitoryActiveCount: count,
            }),
          );
        }),
      );
  }

  @Action(EntryActions.TransitoryFetchEntriesCountForEndUser)
  transitoryFetchEntriesCountForEndUser(
    ctx: StateContext<EntryStateModel>,
    { entityId, viewId }: EntryActions.TransitoryFetchActionEntriesCount,
  ): Observable<any> {
    return this.dataViewsService
      .entriesCountV2({
        entity: entityId,
        id: viewId,
        whereCriteria: this.apiHelper.prepareWhere<DataEntry>({
          state: DataEntryStateEnum.Active,
        }),
      })
      .pipe(
        map((count) => count.count),
        tap((count) => {
          ctx.setState(
            patch<EntryStateModel>({
              transitoryActiveCountForEndUser: count,
            }),
          );
        }),
      );
  }

  @Action(EntryActions.ExportEntries)
  exportEntries(
    ctx: StateContext<EntryStateModel>,
    { payload }: EntryActions.ExportEntries,
  ): Observable<any> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));
    const criteria = payload.dataQuery?.criteria ?? {
      args: [],
      operator: ExpressionTypeEnum.And,
    };
    const filterCriteria = {
      criteria: structuredClone(criteria),
    };

    return this._getFetchEntryCriteriaSource(
      // payload?.search,
      payload.state,
      payload.isValid,
      payload.isObsolete,
      payload.isInvalidRequirement,
    ).pipe(
      switchMap((where) => {
        const filter = this._prepareFilterOfDataEntry(
          {
            where,
            skip: undefined,
            limit: undefined,
            criteria: filterCriteria?.criteria,
          },
          payload.sort,
        );
        const model = {
          entity: payload.entityId,
          templateId: payload.templateId,
          mode: payload.itemsToExport?.length ? 0 : 1, // 0 means selected items, 1 means source
          sourceCollection: payload.collectionId,
          search: payload.search,
          itemsToExport: payload.itemsToExport,
          appliedQuickFilters: payload.appliedQuickFilters,
          context: payload.viewId
            ? new ManagedHttpContext({ publicAccessId: payload.viewId })
            : undefined,
          filterCriteria: filter.filter,
        };

        if (payload.exportType === TemplateExportTypeEnum.Background) {
          return this.exportEntryDataService
            .exportEntriesWithTemplateV2$Json(model)
            .pipe(
              tap((data) => {
                ctx.patchState({
                  transitoryEntriesExportFile: data,
                });
              }),
              finalize(() => {
                ctx.dispatch(new LoadingActions.SetCommandLoading(false));
              }),
            );
        }
        return (
          payload.type === TemplateTypeEnum.Word
            ? this.exportEntryDataService.exportEntriesWithTemplateV2$VndOpenxmlformatsOfficedocumentWordprocessingmlDocument(
                model,
              )
            : payload.type === TemplateTypeEnum.Excel
              ? this.exportEntryDataService.exportEntriesWithTemplateV2$VndOpenxmlformatsOfficedocumentSpreadsheetmlSheet(
                  model,
                )
              : this.exportEntryDataService.exportEntriesWithTemplateV2$Pdf(
                  model,
                )
        ).pipe(
          tap((data) => {
            const type = this._getPayloadFileType(payload.type);

            const _blob = new Blob([data], type);

            ctx.patchState({
              transitoryEntriesExportFile: _blob,
            });
          }),
          finalize(() => {
            ctx.dispatch(new LoadingActions.SetCommandLoading(false));
          }),
        );
      }),
    );
  }

  private _prepareFilterOfDataEntry(
    filter: Pick<Filter<DataEntry>, 'where' | 'skip' | 'limit' | 'criteria'>,
    sort?: Sort[],
  ) {
    const { where, skip, limit, criteria } = filter;

    return this.apiHelper.prepareFilter<DataEntry>({
      where,
      skip: skip,
      limit: limit,
      order: sort?.length
        ? sort
            ?.filter((s) => s.sortBy && s.sortDir)
            ?.map((sortField) => {
              const metaFieldsNames = ENTITY_META_FIELDS?.map(
                (f) => f._normalizedName,
              );
              const sortBy = sortField.sortBy;
              if (metaFieldsNames.includes(sortBy)) {
                return `${sortBy} ${sortField.sortDir?.toUpperCase()}`;
              } else {
                return `${
                  sortBy.startsWith('value') ? sortBy : `value.${sortBy}`
                } ${sortField.sortDir?.toUpperCase()}`;
              }
            })
        : undefined,
      criteria: criteria?.args?.length ? criteria : undefined,
    });
  }

  private _getFetchEntryCriteriaSource(
    entryState: DataEntryStateEnum,
    entryValid: boolean,
    entryObsolete: boolean,
    entryInvalidRequirement: boolean,
  ): Observable<Where<any>> {
    return of(
      buildEntriesStatusFilter({
        state: entryState,
        includeValid: entryValid,
        includeObsolete: entryObsolete,
        includeInvalidRequirement: entryInvalidRequirement,
      }),
    );
  }

  private _getTransitoryEntriesForCollection({
    entityId,
    collectionId,
    filter,
    withCount,
    totalCountWhere,
    appliedQuickFilters,
    appliedTagsFilters,
    search,
  }: {
    withCount: boolean;
    filter: any;
    entityId: string;
    collectionId: string;
    totalCountWhere: any;
    appliedQuickFilters: string[];
    appliedTagsFilters: string[];
    search?: string;
  }): Observable<{
    count: number;
    entriesResponse: DataEntriesResponseV3;
  }> {
    const params = {
      id: collectionId,
      entity: entityId,
      appliedQuickFilters,
      appliedTagsFilters,
      ...filter,
      search,
    };
    return forkJoin({
      entriesResponse: this.collectionService.findEntriesV2(params).pipe(
        map(
          (res) =>
            ({
              entries: res.entries,
              tags: res.tags,
            }) as DataEntriesResponseV3,
        ),
      ),
      count: withCount
        ? this.collectionService
            .findEntriesCountV2({
              entity: entityId,
              id: collectionId,
              where: totalCountWhere,
              appliedQuickFilters: appliedQuickFilters,
              search,
            })
            .pipe(map((count) => count.count))
        : of(0),
    });
  }

  private getPatchedEntryFieldValue({
    fieldName,
    fieldValue,
    fieldIndex,
  }: {
    fieldName: string;
    fieldValue: unknown;
    fieldIndex?: number | string;
  }): {
    op: 'add' | 'replace' | 'remove';
    path: string;
    value?: any;
  } {
    const getPathFromPayload = (): string => {
      const fieldPath = fieldName;
      fieldIndex = fieldIndex ?? '';
      const paths = fieldPath.split('.');
      if (!paths || !paths?.length) {
        return null;
      }
      if (paths.length === 1) {
        return '/' + paths[0];
      }
      if (
        fieldIndex === null ||
        fieldIndex === undefined ||
        fieldIndex === ''
      ) {
        return '/' + fieldPath.split('.').join('/');
      }
      return `/${paths[0]}/${fieldIndex}/${paths[1]}`;
    };

    return {
      op: 'replace',
      path: getPathFromPayload(),
      value: fieldValue,
    };
  }
  private getPatchedEntryFieldValueV3({
    fieldName,
    fieldValue,
    fieldIndex,
    rootReference,
  }: {
    fieldName: string;
    fieldValue: unknown;
    fieldIndex?: number | string;
    rootReference?: {
      normalizedName: string;
      dataEntry: string;
    };
  }): {
    patchOperations: {
      op: 'add' | 'replace' | 'remove';
      path: string;
      value?: any;
    }[];
    mode: 0 | 1;
    referencedEntryId?: string;
    fieldNormalizedName?: string;
  } {
    const getPathFromPayload = (): string => {
      const fieldPath = fieldName;
      fieldIndex = fieldIndex ?? '';
      const paths = fieldPath.split('.');
      if (!paths || !paths?.length) {
        return null;
      }
      if (paths.length === 1) {
        return '/' + paths[0];
      }

      if (
        !!rootReference ||
        fieldIndex === null ||
        fieldIndex === undefined ||
        fieldIndex === ''
      ) {
        return '/' + fieldPath.split('.').join('/');
      }
      return `/${paths[0]}/${fieldIndex}/${paths[1]}`;
    };

    return {
      patchOperations: [
        {
          op: 'replace',
          path: getPathFromPayload(),
          value: fieldValue,
        },
      ],
      mode: rootReference ? 1 : 0,
      referencedEntryId: rootReference?.dataEntry,
      fieldNormalizedName: rootReference?.normalizedName,
    };
  }
  private getEntryEntityFromState(
    ctx: StateContext<EntryStateModel>,
    entityId: string,
    entryId: string,
  ): DataEntityPresenterModel {
    const entityV2 =
      ctx.getState().presentedEntry[EntryState.getKey(entityId, entryId)]
        ?.entity;
    if (entityV2) return { ...entityV2 };
    return {
      ...this.store.selectSnapshot(EntityState.presentedEntity(entityId)),
    };
  }

  private _getPayloadFileType(templateType: TemplateTypeEnum) {
    switch (templateType) {
      case TemplateTypeEnum.Excel:
        return {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        };
      case TemplateTypeEnum.Word:
        return {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        };
      case TemplateTypeEnum.PDF:
        return { type: 'application/pdf' };
    }
  }
  @Action(EntryActions.FetchEntryEmails)
  getEntryEmails(
    ctx: StateContext<EntryStateModel>,
    { entityId, entryId }: EntryActions.FetchEntryEmails,
  ): Observable<any> {
    return this.entryDataService
      .getEmails({ entity: entityId, id: entryId })
      .pipe(
        map((res) =>
          res.map((entryEmail) =>
            this.entryEmailFactory.createPresenterModel(entryEmail),
          ),
        ),
        tap((emails) => {
          ctx.setState(
            patch<EntryStateModel>({
              presentedEntryEmails: emails,
            }),
          );
        }),
      );
  }

  @Action(EntryActions.FetchEntryDiscussionsAttachments)
  getEntryDiscussionsAttachments(
    ctx: StateContext<EntryStateModel>,
    { entityId, entryId }: EntryActions.FetchEntryDiscussionsAttachments,
  ): Observable<any> {
    return this.commentControllerService
      .getCommentsAttachmentsRelatedTo({
        entity: entityId,
        relatedToId: entryId,
      })
      .pipe(
        tap((discussionsAttachments) => {
          ctx.setState(
            patch<EntryStateModel>({
              presentedEntryDiscussionsAttachments: discussionsAttachments,
            }),
          );
        }),
      );
  }

  @Action(EntryActions.CheckIsTemplateEntryUsed)
  checkIsPresentedTemplateEntryUsed(
    ctx: StateContext<EntryStateModel>,
    { entryId }: EntryActions.CheckIsTemplateEntryUsed,
  ): Observable<any> {
    const presentedEntries = ctx.getState().presentedEntry;
    presentedEntries;
    return this.templatesControllerService.isTemplateUsed({ id: entryId }).pipe(
      tap((response) => {
        ctx.setState(
          patch<EntryStateModel>({
            isPresentedTemplateEntryUsed: response,
          }),
        );
      }),
    );
  }

  @Action(EntryActions.ClearContextState)
  clearContextState(
    ctx: StateContext<EntryStateModel>,
    { entity, viewId, reportId, collectionId }: EntryActions.ClearContextState,
  ): void {
    const contextKey = EntryState.getContextKey(
      entity,
      collectionId ?? reportId ?? viewId,
    );

    ctx.setState(
      patch<EntryStateModel>({
        contextAppliedFilter: patch({ [contextKey]: null }),
        contextPresentedDraftEntries: patch({ [contextKey]: null }),
        contextPresentedEntries: patch({ [contextKey]: null }),
        contextPresentedEntriesTags: patch({ [contextKey]: null }),
        contextPresentedKanbanEntries: patch({ [contextKey]: null }),
        contextPresentedDraftKanbanEntries: patch({ [contextKey]: null }),
        contextUserCollectionEntries: patch({ [contextKey]: null }),
        contextUserCollectionEntriesTags: patch({ [contextKey]: null }),
        contextUserCollectionEntriesTotalCount: patch({ [contextKey]: null }),
      }),
    );
  }

  @Action(EntryActions.ClearRelationContextState)
  ClearRelationContextState(
    ctx: StateContext<EntryStateModel>,
    { relationId, viewId }: EntryActions.ClearRelationContextState,
  ): void {
    const contextKey = EntryState.getContextKey(relationId, viewId);

    ctx.setState(
      patch<EntryStateModel>({
        contextRelationEntries: patch({ [contextKey]: null }),
        contextRelationEntriesAppliedFilters: patch({ [contextKey]: null }),
        contextPresentedRelationKanbanEntries: patch({ [contextKey]: null }),
      }),
    );
  }
}
